import { HEAD_VERBS } from './head-verbs.js';
// Emberline web reader.
//
// No framework and no build step. The whole client is one module against the Go API, which keeps
// the deploy story to "serve a directory" and makes the API the only contract to hold stable
// while the mobile apps are built against the same one.
//
// State lives in the URL. #/story/001-emberline/3 is a real address a reader can send someone,
// and back/forward work without any history bookkeeping of our own.

const API = window.AIC_API || (location.port === '5173' ? 'http://localhost:8080' : '');

// ── session ────────────────────────────────────────────────────────────────
// The token is the only thing persisted. Progress lives server-side so it follows the reader to
// the phone, which is the promise the sign-in copy makes.
const KEY = 'aic.token';
const EDITION_KEY = 'aic.edition';
let token = localStorage.getItem(KEY) || '';
let me = null;

// EDITION is the reader's choice of how to consume a chapter, and it is a real fork in the data,
// not a skin. The manga edition is the LETTERED art: the narration and dialogue are drawn into
// the picture, so printing them again underneath prints the whole chapter twice — which is what
// this reader did until the API started saying which panels it had served. The novel edition is
// the prose with the scene direction as staging, and it is the ONLY edition available for the
// vast majority of chapters, which are authored far ahead of what the GPU has drawn.
let edition = localStorage.getItem(EDITION_KEY) || 'manga';

// MATURE IS OPT-IN. The server defaults to teen and applies the ceiling itself, so omitting this
// shows the safe set rather than everything — the client's copy is a convenience, never the gate.
const AGE_KEY = 'aic.maxAge';
let maxAge = localStorage.getItem(AGE_KEY) || 'teen';
let query = '';

const el = (id) => document.getElementById(id);
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

async function api(path, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(API + path, { ...opts, headers });
  const body = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) throw Object.assign(new Error(body?.error || res.statusText), { status: res.status, body });
  return body;
}

// ── referral ───────────────────────────────────────────────────────────────
// A REFERRAL CODE HAS TO SURVIVE THE WHOLE VISIT, NOT JUST THE FIRST PAINT.
//
// The link is /?ref=<referrer id>, and nobody signs up the instant they land -- they read a
// chapter or two first, and every hash route change and reload would drop a value held only in
// memory. So it is captured once and persisted, then sent with the eventual register call.
//
// It is also stripped from the visible URL after capture: leaving it there means the reader copies
// a link containing SOMEBODY ELSE'S code when they share the page, quietly crediting the wrong
// person for the rest of the chain.
const REF_KEY = 'aic.ref';

function captureRef() {
  try {
    const ref = new URLSearchParams(location.search).get('ref');
    if (!ref) return;
    localStorage.setItem(REF_KEY, ref.slice(0, 64));
    // Drop it from the address bar without touching the hash route.
    const clean = location.pathname + location.hash;
    history.replaceState(null, '', clean);
  } catch { /* private mode: a referral is not worth an error on a reader's first paint */ }
}
captureRef();

function takeRef() {
  try {
    const ref = localStorage.getItem(REF_KEY) || '';
    // Consumed, not kept: a code that survives the signup would be sent again by the next account
    // created in this browser, which is how one link pays twice.
    localStorage.removeItem(REF_KEY);
    return ref;
  } catch { return ''; }
}

// ── crash reporting ────────────────────────────────────────────────────────
// AN UNCAUGHT ERROR HERE IS AS INVISIBLE AS A SERVER PANIC WAS: the page
// half-renders, the reader sees a blank shelf, and nothing anywhere records it.
//
// IT POSTS TO OUR OWN ORIGIN, NOT TO SENTRY. @sentry/browser would need
// script-src widened to a third party that can then run JavaScript in this page,
// connect-src widened to its ingest host, ~30KB of bundle, and the DSN shipped
// inside a committed artifact. Posting to /api/client-errors needs none of that:
// connect-src 'self' already allows it, and the DSN stays on the server, which
// forwards through the same reporter it uses for its own panics.
//
// THREE RULES, ALL LEARNED FROM HOW THIS GOES WRONG:
//   1. Never throw. This runs inside window.onerror; an error in the reporter
//      re-enters the reporter.
//   2. Never report our own POST failing. That is the loop that turns one broken
//      page into a flood against the server's own error budget.
//   3. Cap and de-duplicate per page. A render error inside a loop fires
//      thousands of times; the first one is the whole signal.
const CRASH_MAX_PER_PAGE = 3;
const crashSeen = new Set();
let crashSent = 0;
// Guards rule 2: set while our own POST is in flight.
let crashPosting = false;

function reportCrash(kind, message, extra = {}) {
  try {
    if (crashPosting || crashSent >= CRASH_MAX_PER_PAGE) return;
    const msg = String(message ?? '').slice(0, 512);
    if (!msg.trim() || crashSeen.has(msg)) return;
    crashSeen.add(msg);
    crashSent += 1;
    crashPosting = true;
    // PATHNAME ONLY -- never location.href. The query string and hash carry
    // what the reader typed and which story they are reading. The server
    // strips a query string again if one arrives, but it should not have to.
    const body = JSON.stringify({
      kind,
      message: msg,
      path: location.pathname,
      line: extra.line || 0,
      col: extra.col || 0,
      stack: String(extra.stack ?? '').slice(0, 4096),
      release: window.AIC_RELEASE || '',
    });
    // keepalive so a report survives the navigation that often follows a
    // crash. No .then() chain that could throw, and the rejection is swallowed
    // deliberately -- see rule 2.
    fetch(API + '/api/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {}).finally(() => { crashPosting = false; });
  } catch {
    // Rule 1. There is nothing useful to do, and doing anything risks recursion.
    crashPosting = false;
  }
}

window.addEventListener('error', (e) => {
  // A failed <img>/<script> load also fires this, with no .error object. Those
  // are not crashes and would drown the real ones.
  if (!e || !e.message) return;
  reportCrash('error', e.message, {
    line: e.lineno, col: e.colno, stack: e.error && e.error.stack,
  });
});

window.addEventListener('unhandledrejection', (e) => {
  const r = e && e.reason;
  reportCrash('unhandledrejection', (r && (r.message || r)) || 'unhandled rejection', {
    stack: r && r.stack,
  });
});

// ── analytics ──────────────────────────────────────────────────────────────
// Names come from platform/analytics/events.go. They are written out rather than imported because
// there is no build step; the server-side taxonomy test is what keeps the two honest.
// A JOURNEY NEEDS AN IDENTITY, AND EVERY EVENT WAS ANONYMOUS AND UNLINKED.
//
// The owner asked to "track every user journey when needed". Every piece was already there — a
// curated taxonomy, an ingest that rejects unknown names, a tracker writing JSON lines that Cloud
// Run ships to Cloud Logging, and GA4 beside it — except the one thing that makes a sequence of
// events a JOURNEY. `track()` sent {event, props, ts}. The server attaches a user id ONLY for a
// signed-in reader, and almost nobody is signed in when they are deciding whether to read, so the
// events that matter most — app.opened, story.viewed, paywall.shown, paywall.dismissed — arrived
// with no way to tell one reader from a thousand. The trace_id on each log line is per REQUEST, so
// it cannot stitch two events either.
//
// FIRST-PARTY, AND NO PERSONAL DATA. Both ids are random, mean nothing outside this site, and are
// never joined to anything identifying. DO NOT TRACK IS HONOURED: with it on, no id is generated,
// nothing is stored, and events still flow unlinked — the same rule gaInit() already follows, and
// the honest reading of a reader who asked not to be followed around.
const DNT = () => navigator.doNotTrack === '1' || window.doNotTrack === '1';
const VISITOR_KEY = 'aic.vid';
const SESSION_KEY = 'aic.sid';
const SESSION_TOUCH = 'aic.sid.at';
// 30 minutes, the convention every analytics product uses, so "sessions" here mean what a reader
// of the numbers will assume they mean.
const SESSION_IDLE_MS = 30 * 60 * 1000;

const randomID = () => (crypto.randomUUID?.() || String(Math.random()).slice(2) + Date.now().toString(36));

// visitorId is stable across visits; sessionId restarts after 30 minutes idle. Both return '' under
// DNT, and '' is what the server stores — an event with no identity rather than a fabricated one.
function visitorId() {
  if (DNT()) return '';
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) { v = randomID(); localStorage.setItem(VISITOR_KEY, v); }
    return v;
  } catch { return ''; }   // private mode: no id, still counted
}

function sessionId() {
  if (DNT()) return '';
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(SESSION_TOUCH) || 0);
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid || now - last > SESSION_IDLE_MS) { sid = randomID(); sessionStorage.setItem(SESSION_KEY, sid); }
    sessionStorage.setItem(SESSION_TOUCH, String(now));
    return sid;
  } catch { return ''; }
}

// FIRST TOUCH, NOT LAST. Stored once and never overwritten, because the campaign that BROUGHT a
// reader is the one worth paying for — last-touch credits whatever they clicked on their way back,
// which is usually the site itself. Kept for the marketing launch: without this, spend cannot be
// told apart from luck.
const FIRST_TOUCH_KEY = 'aic.ft';
function firstTouch() {
  if (DNT()) return null;
  try {
    const saved = localStorage.getItem(FIRST_TOUCH_KEY);
    if (saved) return JSON.parse(saved);
    const q = new URLSearchParams(location.search);
    const ft = {};
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const v = q.get(k);
      if (v) ft[k] = v.slice(0, 64);
    }
    // The referring SITE, not the full URL: the host answers "where did they come from" and the
    // path is somebody else's page, which is theirs and not ours to keep.
    if (document.referrer) {
      try {
        const h = new URL(document.referrer).hostname;
        if (h && h !== location.hostname) ft.referrer = h;
      } catch { /* an opaque referrer is no referrer */ }
    }
    if (!Object.keys(ft).length) ft.direct = true;
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(ft));
    return ft;
  } catch { return null; }
}

function track(event, props = {}) {
  const body = {
    event, props, ts: Date.now(),
    visitor_id: visitorId(),
    session_id: sessionId(),
  };
  // Attribution rides on app.opened alone. On every event it would be the same handful of strings
  // repeated thousands of times per reader, for a fact that cannot change after the first visit.
  if (event === 'app.opened') {
    const ft = firstTouch();
    if (ft) body.first_touch = ft;
  }
  navigator.sendBeacon?.(
    `${API}/api/events`,
    new Blob([JSON.stringify(body)], { type: 'application/json' })
  );
}

let toastTimer;
function toast(msg) {
  const t = el('toast');
  t.textContent = msg;
  t.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { t.hidden = true; }, 3200);
}

// ── catalog ────────────────────────────────────────────────────────────────
let catalog = null;
let activeFilter = 'all';
// null = every world, the same meaning 'all' carries for activeFilter. Persistent, so the row can
// show which tab is on — the previous transient approach could not, which is why it had none.
let activeWorld = null;
let activeWorldIds = null;

async function loadCatalog() {
  el('grid').innerHTML = Array.from({ length: 12 }, () => '<div class="skeleton"></div>').join('');
  // /api/catalog returns EVERYTHING, including mature stories; /api/search applies the age
  // ceiling server-side, so the grid is filled from search and the catalogue is needed only for
  // the taxonomy labels.
  //
  // AND ONLY THE LABELS ARE FETCHED. This called /api/catalog and used one field of it: the
  // response is 64 KB and `labels` is 971 bytes, so 63 KB of story records, by_region and by_genre
  // were downloaded, parsed and discarded on every single page load — a second full copy of a
  // catalogue the page had already fetched from /api/search.
  // WORLDS AND FACETS ARE STARTED HERE, NOT AFTER THE AWAIT.
  // They were called below, which meant they did not leave the browser until /api/search had come
  // back. Measured on the live site: search ran 323ms -> 701ms and worlds and facets both started
  // at 703 — a strictly serial waterfall costing about 280ms of dead time for two requests that
  // depend on nothing in the search response. They take max_age and nothing else.
  // The reader's rails join the same parallel batch: they depend on nothing in the search
  // response, and a serial waterfall here already cost about 280ms once.
  const pending = [loadWorlds(), loadFacets(), loadPopular(), loadRecs(), loadShelf()];
  const [labels, found] = await Promise.all([
    api('/api/labels'),
    api(`/api/search?q=${encodeURIComponent(query)}&max_age=${maxAge}`
      + [...activeGenres].map((g) => `&genre=${encodeURIComponent(g)}`).join('')),
  ]);
  catalog = { ...labels, stories: found.stories || [] };
  // SET BEFORE THE FIRST renderGrid BELOW, and that ordering is the whole point: a cover URL built
  // without it is the un-versioned URL the CDN already holds a stale copy of.
  mediaV = found.media_v || mediaV;
  track('catalog.viewed', { stories: catalog.stories.length });
  if (query) track('search.performed', { q: query, results: catalog.stories.length });
  renderFilters();
  renderGrid();
  // Whichever of the two lost the race now has a catalogue to render against — see the guard at
  // the top of renderFilters, which is why running early is safe rather than merely faster.
  Promise.allSettled(pending).then(() => {
    if (facets) renderFilters();
    // The rails are built from these, so the grid is painted again once they land. renderGrid is
    // idempotent and reads the globals, which is why nothing is passed to it.
    renderGrid();
  });
}

// A world is a doorway, not a filter: clicking one searches for its stories rather than narrowing
// the grid in place, so the reader can see what they landed in.

// worldCard is ONE world as a card.
//
// Lifted out of loadWorlds' inline map so it can be sliced out and run alone under node, exactly
// as commentMarkup is by test_comment_escaping.py. The thing worth testing is that no member title
// is unreachable, and that cannot be asserted against an anonymous arrow function inside a DOM
// assignment.
function worldCard(w) {
  // THE CURATED NAME WINS. content/worlds.json spells it "The Red-Thread Metropolis"; deriving it
  // from the id gave "The Red Thread Metropolis" — close enough to look right and wrong in the
  // card's largest type. label() stays as the fallback for a world the file does not list.
  const name = esc(w.name || label(w.id));
  const about = (w.about || '').trim();
  // Titles are the concrete pull: a reader recognises a story name long before a world name.
  const all = (w.titles || []).map(esc);
  const titles = all.slice(0, WORLD_TITLES_SHOWN);
  const rest = all.slice(WORLD_TITLES_SHOWN);
  return `
    <article class="world-card">
      <h3 class="world-card__name">${name}</h3>
      <p class="world-card__count">${w.stories.length} series</p>
      ${about ? `<p class="world-card__about">${esc(about)}</p>` : ''}
      ${titles.length ? `<ul class="world-card__titles">${
        titles.map((t) => `<li>${t}</li>`).join('')
      }</ul>` : ''}
      ${rest.length ? `<details class="world-card__rest">
        <summary>and ${rest.length} more</summary>
        <ul class="world-card__titles">${rest.map((t) => `<li>${t}</li>`).join('')}</ul>
      </details>` : ''}
      <button class="chip world-card__cta" data-world="${esc(w.stories.join(','))}"
        data-world-id="${esc(w.id)}" aria-pressed="${activeWorld === w.id}">${
          activeWorld === w.id ? 'Showing this world' : 'Read this world'}</button>
    </article>`;
}

// How many member titles a world card lists before the rest go behind a disclosure.
//
// WHY THERE IS A DISCLOSURE AT ALL NOW. The card listed four and then a dead list item reading
// "and 7 more" — a sentence, not a control, so the reader was told how much they could not see and
// given no way to see it. The worlds run from five to eleven stories, so that line was hiding
// between one and seven titles on every card in the row. <details> costs no JavaScript, is
// keyboard-operable and screen-reader-announced for free, and collapses again.
const WORLD_TITLES_SHOWN = 4;
let worlds = null;
async function loadWorlds() {
  if (!worlds) {
    worlds = await api(`/api/worlds?max_age=${maxAge}`).then((d) => d.worlds).catch(() => []);
  }
  const big = worlds.filter((w) => w.stories.length > 1);
  el('worlds').hidden = !big.length;
  // The story count is gone from here too. A world is a doorway — "The Red Thread Metropolis" is
  // the invitation, and "3" beside it is a disclosure about how young the catalogue is, made at
  // the exact moment a reader was interested.
  // SAME TREATMENT AS THE OTHER TWO FILTER ROWS: one line, All first, overflow behind More.
  //
  // This row wrapped onto as many lines as it needed, with no All chip and no selected state,
  // because a world was treated as a doorway rather than a filter — clicking one narrowed the grid
  // transiently and the selection vanished on the next render. Three rows stacked on top of each
  // other behaving three different ways is the reader's problem, not an architecture distinction:
  // they read as one control group and should work like one.
  // A CARD PER WORLD, not a name chip.
  //
  // The chip row showed only "the thin veil", which means nothing to a reader who has not read one
  // of its stories — while the API had been returning an `about` paragraph and the member story
  // titles all along. Showing them is the difference between a filter and an invitation.
  //
  // No fitFilterRow here: this is a wrapping grid BELOW the catalogue, not a one-line control
  // strip, so there is nothing to overflow into a More menu.
  el('worlds-row').innerHTML = big.map(worldCard).join('');
}

// catalog?. and not catalog. — a deep link goes straight to #/story/... or #/read/..., which
// never loads the catalog, so this ran against null on exactly the arrival path that matters
// most: the one a reader gets sent. It threw before the chapter rendered and the page came up
// blank with the error in a toast that had already timed out.
function label(id) {
  return catalog?.labels?.[id] || id.replace(/(^|[-_])(\w)/g, (_, s, c) => (s ? ' ' : '') + c.toUpperCase());
}

// Facets come from the SERVER, with counts computed after the age gate.
//
// The filter row used to be built from whatever stories happened to be on screen, which meant it
// described the current result set rather than the catalogue — the genres you could pick shrank as
// soon as you picked one. It also offered region only, so 18 genres sat in the data unreachable
// while a manga site let you filter by country of origin and nothing else.
let facets = null;
let activeGenres = new Set();

function genreLabelOf(id) {
  const hit = (facets?.genres || []).find((g) => g.id === id);
  if (hit) return hit.label;
  return String(id).split('_').map((w) => w ? w[0].toUpperCase() + w.slice(1) : w).join(' ');
}

// Must match the `gap` on .filters--line. Used to measure how many chips fit on one line.
const FILTER_GAP_PX = 7;

// A tally of which genres this reader has actually turned on, so the visible six become theirs
// rather than the catalogue's. Kept client-side: it is a browsing preference, not an account fact,
// and it should not need a login to work or follow anyone to a server.
const GENRE_USE_KEY = 'aic.genreUse';
let genreUse = {};
try { genreUse = JSON.parse(localStorage.getItem(GENRE_USE_KEY) || '{}') || {}; } catch { genreUse = {}; }

async function loadFacets() {
  try {
    facets = await api(`/api/facets?max_age=${maxAge}`);
  } catch {
    facets = { genres: [], regions: [] };
  }
  renderFilters();
}

// THE OVERFLOW CONTROL IS NAMED AFTER ITS ROW — "Origins", "Genres" — not "More".
//
// Two stacked rows both ending in a button called "More" say nothing about what is behind either,
// and the rows have no other label: a reader sees a strip of country names and a strip of genre
// names with no heading telling them which question each answers. The control is the only place a
// name can go without spending a line on a heading, so it carries one.
//
// NO "ALL" CHIP ON ANY ROW. Nothing selected IS everything, and pressing the selected tab again
// clears it. An All chip spends a permanent slot on the one line these rows get, sits a thumb away
// from More (two controls both starting with "All" — one clearing, one opening a menu), and states
// a default the empty row already states.
function renderFilters() {
  // A CATALOGUE IS NOT GUARANTEED HERE ANY MORE. loadFacets calls this the moment /api/facets
  // answers, and facets is now started BEFORE the search it used to wait for — so on a fast facets
  // response this runs first and `catalog.stories` would throw on undefined, killing the render.
  // It is called again once the catalogue lands, so returning early loses nothing. Same reason
  // worldCard reads catalog?. rather than catalog.
  if (!catalog?.stories) return;
  const regions = [...new Set(catalog.stories.map((s) => s.region).filter(Boolean))].sort();
  // Same overflow control as genres. Six regions fit most laptops and three fit a phone, so the
  // row needs the same treatment rather than being assumed short enough.
  //
  // Labelled "More", never "All origins": the reset chip on this same row is called All, and two
  // controls a thumb apart both starting with "All" — one clearing the filter, one opening a menu —
  // is a coin flip every time.
  const regionRow = `
    <details class="more" id="region-more" hidden>
      <summary>Origins</summary>
      <div class="more__panel"></div>
    </details>` + regions
    // aria-pressed, not role="tab"/aria-selected: these narrow a grid, they do not switch panels,
    // and the site has no tabpanel for them to control.
    .map((r) => `<button class="chip" data-filter="${esc(r)}"
        aria-pressed="${r === activeFilter}">${esc(label(r))}</button>`)
    .join('');
  // Counts are shown because a shelf with two stories and a shelf with eleven are different
  // invitations, and a reader deciding where to spend an evening should be able to tell.
  // NO COUNTS. They were shown on the theory that a shelf of eleven and a shelf of two are
  // different invitations — which is true, and exactly the problem: a young catalogue advertising
  // "Horror 1" is telling every reader how little there is. The count belongs to us, for deciding
  // what to render next; the reader wants to know the genre exists.
  const chip = (g) => `<button class="chip chip--genre" data-genre="${esc(g.id)}"
        aria-pressed="${activeGenres.has(g.id)}">${esc(g.label)}</button>`;

  // Genre label for a card, from the facets the server already sent. Falls back to the raw id
  // de-underscored, so a genre added by an author shows up immediately rather than as a blank.
  

  // ORDERED BY WHAT THIS READER ACTUALLY USES, then by catalogue size.
  //
  // Eighteen genres do not fit on one line and never will. Which six are worth the line is not a
  // fixed answer: it depends on who is looking. A genre this reader has picked before is promoted
  // ahead of a bigger shelf they have ignored, and anything selected right now is always visible —
  // a filter that hides the very thing you turned on is how a reader concludes it is broken.
  const ranked = [...(facets?.genres || [])].sort((a, b) => {
    if (activeGenres.has(a.id) !== activeGenres.has(b.id)) return activeGenres.has(a.id) ? -1 : 1;
    const ua = genreUse[a.id] || 0, ub = genreUse[b.id] || 0;
    if (ua !== ub) return ub - ua;
    return b.count - a.count;
  });
  // EVERY chip is rendered, and the ones that do not fit are moved into the overflow AFTER
  // measuring. A fixed count cannot be right: six chips fit a laptop, three fit a phone in
  // portrait and eleven fit an ultrawide, and the genre labels are different lengths anyway.
  //
  // The overflow control sits FIRST so it occupies a known, fixed slot at the start of the line;
  // trailing it would mean the last chip and the control compete for the same remainder, and the
  // control would jump around as the set changes.
  const genreRow = `
    <details class="more" id="genre-more" hidden>
      <summary>Genres</summary>
      <div class="more__panel"></div>
    </details>` + ranked.map(chip).join('');

  el('filters').innerHTML = regionRow;
  const gEl = el('genre-filters');
  if (gEl) {
    gEl.innerHTML = genreRow;
    gEl.hidden = !ranked.length;
    fitFilterRow(gEl);
  }
  fitFilterRow(el('filters'));
}

// fitFilterRow keeps a filter row to ONE line, moving whatever overflows into its "All" panel.
//
// Measured rather than guessed, because the answer depends on viewport width, on the length of the
// labels, and on the reader's font size. Chips are returned to the line before measuring so the
// row can GROW again when a window is widened — an overflow that only ever fills is a row that
// never recovers from one narrow moment.
function fitFilterRow(row) {
  if (!row || row.hidden) return;
  const more = row.querySelector('.more');
  const panel = more?.querySelector('.more__panel');
  if (!more || !panel) return;
  while (panel.firstChild) more.after(panel.firstChild);

  more.hidden = false;                       // measure with the control present, not without it
  const limit = row.clientWidth - more.offsetWidth - FILTER_GAP_PX;
  let used = 0;
  const chips = [...row.children].filter((c) => c !== more);
  // PINNED CHIPS NEVER OVERFLOW.
  //
  // "Everything" is how a reader CLEARS a filter, and an active chip is the one thing they can see
  // is on. Both were being pushed into the panel on a narrow window — so the way out of a filter
  // was hidden behind the control that had hidden it, which is how a reader concludes the site is
  // stuck. Pinned chips are laid out first and always keep their place on the line.
  // Only the ACTIVE chip is pinned now. The rule used to pin data-filter="all" as well, because an
  // All chip was the reader's way out and must never hide behind the control that hid it. There is
  // no All chip on any row any more — clearing is done by pressing the selected tab again — so the
  // thing that must stay visible is precisely the selected one.
  const pinned = (c) => c.getAttribute('aria-selected') === 'true'
    || c.getAttribute('aria-pressed') === 'true';
  for (const c of chips) {
    if (pinned(c)) used += c.offsetWidth + FILTER_GAP_PX;
  }
  const overflow = [];
  for (const c of chips) {
    if (pinned(c)) continue;
    used += c.offsetWidth + FILTER_GAP_PX;
    if (used > limit) overflow.push(c);
  }
  // Pinned chips move to the FRONT of the line, not merely onto it. Reserving their width kept
  // them visible but left them wherever the ranking put them — so "All" sat after two genres it
  // is supposed to reset. A control that clears the others belongs before them.
  [...chips].reverse().filter(pinned).forEach((c) => more.after(c));
  overflow.forEach((c) => panel.appendChild(c));
  more.hidden = overflow.length === 0;
  // No count on the control either — same reason as the chips.
}

// Re-fit on resize and on orientation change. Debounced: a drag across the screen fires this
// continuously, and each call reflows the row.
let fitTimer = null;
window.addEventListener('resize', () => {
  clearTimeout(fitTimer);
  fitTimer = setTimeout(() => {
    fitFilterRow(el('genre-filters'));
    fitFilterRow(el('filters'));
    // worlds-row is no longer fitted: it became a wrapping card grid below the catalogue rather
    // than a one-line control strip, so it has no More menu to overflow into.
  }, 120);
});

// A COVER THAT 404s MUST NOT SHOW A BROKEN-IMAGE ICON.
//
// This used to be an inline onerror="" attribute, which the site's own Content-Security-Policy
// blocks — script-src 'self' refuses inline handlers — so the fallback never ran and a story with
// no cover.png rendered a broken image. Found because 116-western-quiet-frequency-scifi has no
// cover at all: the console said the handler was blocked, not that the image was missing.
//
// A delegated listener on the grid, in the capture phase because `error` does not bubble. One
// listener for the whole grid rather than one per card, so it survives every re-render.
function markMissingCovers(root) {
  root.addEventListener('error', (e) => {
    const img = e.target;
    if (img && img.tagName === 'IMG') img.closest('.card__art')?.classList.add('card__art--empty');
  }, true);
}

// THE RAILS. Definitions, so adding one is a row in this table and not a new function.
//
// `pick` returns the stories that belong; `rank` orders them. Every rail states the claim its label
// makes, because the reason this is a table rather than a "Top 10" sort is that we have nothing to
// support a popularity claim — see the note on #rails in index.html.
// POPULARITY IS FETCHED, NOT DERIVED. /api/popular ranks by distinct readers and refuses to answer
// until enough of them exist — see backend/internal/api/popular.go. `ready:false` means "we are not
// counting yet", which is a different statement from "nothing is popular", and the rails render
// nothing in either case rather than showing a ranking of two.
let popular = null;

async function loadPopular() {
  if (popular) return popular;
  try {
    popular = await api('/api/popular');
  } catch {
    popular = { popular: [], near: [], ready: false, reason: 'unavailable' };
  }
  return popular;
}

// THE READER'S OWN RAILS, AND THE SHELF THAT USED TO BE A SEPARATE SCREEN.
//
// The owner asked for the shelf to be "on the home apge itself directly, that way home page will
// be organised better", beside "just added, you might like..., trending near you, Top picks, liked
// by you". Just added and trending already existed. The other three come from the server:
// /api/recommend ranks them in backend/internal/recommend, which is the same scorer the library's
// suggestions use — one ranking, not two that drift.
//
// /api/recommend answers for a signed-OUT reader too (Top picks needs no history), so it is loaded
// with the rest of the boot. The shelf needs a session and is skipped without one.
let recs = null;
let shelf = null;

async function loadRecs() {
  if (recs) return recs;
  try {
    recs = await api('/api/recommend');
  } catch {
    // The rails simply do not build. A home page missing a rail is a smaller failure than a home
    // page that stops rendering because a ranking was unavailable.
    recs = { you_might_like: [], top_picks: [], liked_by_you: [] };
  }
  return recs;
}

async function loadShelf() {
  if (!token) { shelf = null; return null; }
  if (shelf) return shelf;
  try {
    shelf = await api('/api/me/library');
  } catch {
    shelf = { in_progress: [], saved: [] };
  }
  return shelf;
}

// The server sent an ordered list of ids; these mirror popularIDs/popularIndex so a server-ranked
// rail is defined the same way whichever endpoint ranked it.
function recIDs(which) {
  const list = (recs && recs[which]) || [];
  return new Set(list.map((r) => (typeof r === 'string' ? r : r.id)));
}

function recIndex(which, id) {
  const list = (recs && recs[which]) || [];
  const i = list.findIndex((r) => (typeof r === 'string' ? r : r.id) === id);
  return i < 0 ? Number.MAX_SAFE_INTEGER : i;
}

// The reason the scorer gave, so "You might like" can say why instead of asking to be believed.
function recBecause(id) {
  const row = ((recs && recs.you_might_like) || []).find((r) => r.id === id);
  return row && row.because ? row.because : '';
}

function shelfIDs(which) {
  const list = (shelf && shelf[which]) || [];
  return new Set(list.map((it) => it.id));
}

function shelfIndex(which, id) {
  const list = (shelf && shelf[which]) || [];
  const i = list.findIndex((it) => it.id === id);
  return i < 0 ? Number.MAX_SAFE_INTEGER : i;
}

function shelfRow(which, id) {
  return ((shelf && shelf[which]) || []).find((it) => it.id === id);
}

const RAILS = [
  {
    id: 'watch',
    title: 'Watch the whole thing',
    note: 'Every open chapter narrated — play straight through',
    // COMPLETE, not merely non-empty. 82 of 151 stories have a cut for every chapter they open and
    // the rest are part-narrated, so "has any video" would put a story with one episode beside one
    // with fifteen under a heading that promises a watch. The claim the label makes is the claim
    // the filter enforces.
    pick: (s) => (s.watch_count || 0) > 0 && s.watch_count === s.chapter_count,
    rank: (a, b) => (b.chapter_count || 0) - (a.chapter_count || 0)
      || a.title.localeCompare(b.title),
  },
  // NO "DEEPEST STORIES" RAIL, AND THE DATA IS THE REASON. Measured over the live catalogue:
  // 150 of 151 stories open exactly 15 chapters and one opens 80. There is nothing to order, so a
  // rail headed "long enough to disappear into" would rank fifteen against fifteen and put the
  // winner first. It was written, it refused to build under the four-item minimum below, and it is
  // removed rather than left to appear the day a second long story lands and make a claim off two
  // data points.
  {
    id: 'popular',
    title: 'What everyone is reading',
    note: 'Ranked by how many readers opened it',
    // Order comes from the SERVER, which counted it. The client must not re-sort a ranking it did
    // not compute — that is how two surfaces come to show different "most popular" stories.
    pick: (s) => popularIDs('popular').has(s.id),
    rank: (a, b) => popularIndex('popular', a.id) - popularIndex('popular', b.id),
  },
  {
    id: 'near',
    // The country comes from the edge, so the heading can name it rather than saying "near you",
    // which a reader cannot verify and half of them will find wrong.
    title: () => `Popular in ${regionName(popular?.country)}`,
    note: 'What readers in your country are opening',
    pick: (s) => popularIDs('near').has(s.id),
    rank: (a, b) => popularIndex('near', a.id) - popularIndex('near', b.id),
  },
  {
    id: 'new',
    // "Newest" is a claim about time and the catalogue carries no dates, so this says what is true:
    // these are the most recently ADDED, which is the order the catalogue itself is in.
    title: 'Just added',
    note: 'The latest stories to arrive',
    pick: () => true,
    rank: (a, b) => catalog.stories.indexOf(b) - catalog.stories.indexOf(a),
  },
];

// THE READER'S RAILS GO FIRST, and the order is the answer to "organised better": what you were
// already doing, then what you chose to keep, then the three that suggest something new, then the
// two that are about everybody else. A rail only appears when it has four items (see renderRails),
// so a signed-out reader sees the catalogue rails and nothing claiming to be personal.
RAILS.unshift(
  {
    id: 'continue',
    title: 'Continue reading',
    note: 'Where you left off',
    // ONE IS ENOUGH FOR A SHELF. The four-item floor below exists so a DISCOVERY rail does not
    // repeat the grid back at the reader; a reader with two books on the go is not being shown a
    // ranking, they are being shown their own place in them, and hiding it until they have four
    // would be the home page withholding the thing it was asked to put first.
    min: 1,
    pick: (s) => shelfIDs('in_progress').has(s.id),
    rank: (a, b) => shelfIndex('in_progress', a.id) - shelfIndex('in_progress', b.id),
  },
  {
    id: 'saved',
    title: 'Your shelf',
    note: 'Stories you saved',
    min: 1,
    pick: (s) => shelfIDs('saved').has(s.id),
    rank: (a, b) => shelfIndex('saved', a.id) - shelfIndex('saved', b.id),
  },
);
RAILS.push(
  {
    id: 'foryou',
    title: 'You might like',
    note: 'From what you have liked and read',
    // ORDER COMES FROM THE SERVER, which scored it. The client re-sorting a ranking it did not
    // compute is how two surfaces come to disagree about the same reader.
    pick: (s) => recIDs('you_might_like').has(s.id),
    rank: (a, b) => recIndex('you_might_like', a.id) - recIndex('you_might_like', b.id),
  },
  {
    id: 'picks',
    title: 'Top picks',
    note: 'Finished stories first, then what readers chose',
    pick: (s) => recIDs('top_picks').has(s.id),
    rank: (a, b) => recIndex('top_picks', a.id) - recIndex('top_picks', b.id),
  },
  {
    id: 'liked',
    title: 'Liked by you',
    note: 'Everything you have liked',
    min: 1,
    pick: (s) => recIDs('liked_by_you').has(s.id),
    rank: (a, b) => recIndex('liked_by_you', a.id) - recIndex('liked_by_you', b.id),
  },
);
const RAIL_SIZE = 12;

// The ids the server ranked, as a set, so `pick` is O(1) per story.
function popularIDs(which) {
  const list = (popular && popular[which]) || [];
  return new Set(list.map((p) => p.id));
}

function popularIndex(which, id) {
  const list = (popular && popular[which]) || [];
  const i = list.findIndex((p) => p.id === id);
  return i < 0 ? Number.MAX_SAFE_INTEGER : i;
}

// A country code is not a place name. ISO codes a reader recognises get spelled out; the rest fall
// back to the code itself, which is better than a wrong guess or the words "near you".
const REGION_NAMES = {
  IN: 'India', US: 'the US', GB: 'the UK', CA: 'Canada', AU: 'Australia', SG: 'Singapore',
  MY: 'Malaysia', ID: 'Indonesia', PH: 'the Philippines', TH: 'Thailand', VN: 'Vietnam',
  JP: 'Japan', KR: 'Korea', HK: 'Hong Kong', TW: 'Taiwan', BD: 'Bangladesh', PK: 'Pakistan',
  LK: 'Sri Lanka', NP: 'Nepal', AE: 'the UAE', SA: 'Saudi Arabia', ZA: 'South Africa',
  NG: 'Nigeria', KE: 'Kenya', DE: 'Germany', FR: 'France', ES: 'Spain', IT: 'Italy',
  NL: 'the Netherlands', BR: 'Brazil', MX: 'Mexico', TR: 'Turkey',
};
const regionName = (cc) => REGION_NAMES[cc] || cc || 'your country';

function renderRails(stories) {
  const box = el('rails');
  if (!box) return;
  // A rail is worth its heading only if it holds more than the grid below would show anyway.
  const built = RAILS.map((r) => ({ r, items: stories.filter(r.pick).sort(r.rank).slice(0, RAIL_SIZE) }))
    // FOUR FOR A RANKING, ONE FOR THE READER'S OWN SHELF. See the note on the continue rail.
    .filter(({ r, items }) => items.length >= (r.min || 4));
  // A SHELF SURVIVES A NARROW GRID; a ranking does not. With the catalogue filtered to a handful
  // the discovery rails would repeat the grid, but "continue reading" is still the most useful
  // thing on the page — so the shortcut only applies when nothing personal was built.
  const personal = built.some(({ r }) => r.min === 1);
  if (!built.length || (!personal && stories.length < RAIL_SIZE * 2)) {
    // Filtered down to a handful, the rails would repeat the grid back at the reader.
    box.hidden = true;
    const h = el('grid-heading');
    if (h) h.hidden = true;
    return;
  }
  box.innerHTML = built.map(({ r, items }) => `
    <section class="rail" aria-labelledby="rail-${r.id}">
      <h2 class="rail__h" id="rail-${r.id}">${esc(typeof r.title === 'function' ? r.title() : r.title)}<span class="rail__note">${esc(r.note)}</span></h2>
      <ol class="rail__row">${items.map((s, i) => `
        <li><button class="rcard" data-story="${esc(s.id)}">
          <span class="rcard__art"><img src="${coverURL(s.id)}"
            alt="" width="160" height="212" loading="${i < 3 ? 'eager' : 'lazy'}" decoding="async"></span>
          <span class="rcard__t">${esc(s.title)}</span>
          <span class="rcard__line">${cardLine(s)}</span>
          <span class="rcard__m">${railMeta(r.id, s)}</span>
        </button></li>`).join('')}</ol>
    </section>`).join('');
  box.hidden = false;
  const h = el('grid-heading');
  if (h) h.hidden = false;
}

// The number the rail sorted on, shown on the card. A rail that claims an order and then shows
// nothing to check it against is asking to be believed; this shows the reader the same figure the
// sort used.
function railMeta(railId, s) {
  // SHOW THE NUMBER THE RANKING USED. A rail that claims an order and shows nothing to check it
  // against is asking to be believed; the reader count is the whole basis of the claim.
  if (railId === 'popular' || railId === 'near') {
    const row = ((popular && popular[railId]) || []).find((p) => p.id === s.id);
    const n = row ? row.readers : 0;
    return `${n} reader${n === 1 ? '' : 's'}`;
  }
  if (railId === 'watch') {
    return `${s.watch_count} episode${s.watch_count === 1 ? '' : 's'}`;
  }
  // WHERE THE READER ACTUALLY IS, which is the only number worth showing on a shelf rail. The
  // library already sends last_read and chapters, so this costs no extra request.
  if (railId === 'continue') {
    const row = shelfRow('in_progress', s.id);
    if (row && row.last_read) return `chapter ${row.last_read} of ${row.chapters || s.chapter_count}`;
  }
  // THE REASON THE SCORER GAVE. A personal rail that cannot say why is asking to be believed.
  if (railId === 'foryou') {
    const why = recBecause(s.id);
    if (why) return `because you read ${genreLabelOf(why)}`;
  }
  return `${s.chapter_count} chapter${s.chapter_count === 1 ? '' : 's'} free`;
}

function renderGrid() {
  const stories = catalog.stories
    .filter((s) => activeFilter === 'all' || s.region === activeFilter)
    // The world narrows the grid like any other filter now, rather than being applied by mutating
    // the catalogue for a single render and restoring it immediately after.
    .filter((s) => !activeWorldIds || activeWorldIds.has(s.id));
  el('grid').setAttribute('aria-busy', 'false');
  if (!stories.length) {
    // An empty screen is an invitation to act, not a dead end. Name what was searched and what
    // the age ceiling is, because either one may be the reason nothing came back.
    el('grid').innerHTML = `<p class="empty">No stories${query ? ` for "${esc(query)}"` : ''} at
      the ${esc(maxAge)} rating. Try a different search, or raise the rating in the top bar.</p>`;
    return;
  }
  // THE FIRST CARDS ARE NOT LAZY. One of them IS the Largest Contentful Paint element, and
  // Lighthouse measured 57.5s for it while telling us two things plainly: "LCP resources should not
  // use loading=lazy" and "fetchpriority=high should be applied". A lazy image is discovered only
  // after layout, so the single most important byte on the page was queued behind everything else.
  // Four, because that is roughly what is above the fold on a phone; the rest stay lazy, which is
  // what lazy is for.
  //
  // WIDTH AND HEIGHT ARE THE CLS FIX. The 0.08 layout shift is the grid reflowing as each cover
  // arrives with no reserved space. The numbers are the DISPLAYED size Lighthouse reported, so the
  // browser reserves the right box and the CSS still scales it.
  renderRails(stories);
  const EAGER = 4;
  el('grid').innerHTML = stories
    .map((s, i) => {
      const eager = i < EAGER;
      return `<button class="card" data-story="${esc(s.id)}" data-pos="${i}">
        <span class="card__art">
          <img src="${coverURL(s.id)}" alt=""
               width="320" height="425" decoding="async"
               ${eager ? 'loading="eager" fetchpriority="high"' : 'loading="lazy"'}>
        </span>
        <!-- h2, not h3: the only heading above the grid is the hero h1, so h3 skipped a level and
             Lighthouse flagged it. The class is unchanged, so nothing about the look moves. -->
        <h2 class="card__title">${esc(s.title)}</h2>
        <!-- THE LINE THAT SELLS THE STORY, where region and genre used to be.
             "Japanese · Shonen Action" sorts a story; it does not make anybody open one. The owner
             asked for "a one liner clickbaity line below it, which all of them describe the content
             best", and this is an AUTHORED field rather than a cut-down tagline -- see
             scripts/card-lines.py for the measurement that ruled truncation out.
             Region and genre remain for the stories whose line is not written yet, so a partly
             authored catalogue has no holes in it. -->
        <p class="card__line">${cardLine(s)}</p>
        ${editionLine(s.id)}
      </button>`;
    })
    .join('');
  // TEMPORARY (see loadEditions). Painted after the grid so the counts never delay it, and the
  // grid is re-rendered once because the numbers arrive after the first paint.
  if (!editions) loadEditions().then(() => renderGrid());
}

// THE ONE-LINER GOES UNDER EVERY THUMBNAIL, NOT JUST THE BIG CARD.
//
// The owner asked for "a one liner clickbaity line below it" and it was added to the catalogue card
// only. Rails, the Popular/Near-you rows and the signed-in shelves all show the same cover with
// nothing but a title and "149 chapters" — the same thumbnail, doing less work, in the places a
// reader actually browses. One function so the three call sites cannot drift, with the identical
// region-and-genre fallback so a partly authored catalogue has no holes.
function cardLine(s) {
  if (s.card_line) return esc(s.card_line);
  const region = esc(label(s.region || ''));
  const genre = s.genres?.[0] ? ` · ${esc(genreLabelOf(s.genres[0]))}` : '';
  return `${region}${genre}`;
}

// ── story ──────────────────────────────────────────────────────────────────
let story = null;
let access = [];
// The chapters that have a narrated cut, straight from the server. NOT derived here: the client
// cannot see the media tree, and guessing was the bug -- see watchable().
let narrated = [];
let storyShareURL = '';

// EVERY COVER URL CARRIES THE MEDIA VERSION, AND IT IS NOT AN OPTIMISATION.
//
// A cover is served "Cache-Control: public, max-age=31536000, immutable" on the reasoning that the
// art never changes for a given URL. The URL is /media/stories/{id}/cover, so when the art DOES
// change the CDN keeps the old bytes for a year. Measured after re-rendering 36 covers and
// uploading them: the origin answered image/webp, a cache-busted request answered image/webp, and
// the plain request answered image/png with x-cache: HIT.
//
// `media_v` is a hash of the server's media manifest, so it moves exactly when the media set does —
// the same content-hashing scripts/build-client.py does to dist assets, for the same reason.
let mediaV = '';
const coverURL = (id) =>
  `${API}/media/stories/${encodeURIComponent(id)}/cover${mediaV ? `?v=${mediaV}` : ''}`;

async function loadStory(id, pos) {
  const data = await api(`/api/stories/${encodeURIComponent(id)}`);
  story = data.story;
  access = data.access || [];
  narrated = data.watch || [];
  // Built by the server -- see ChapterContent.ShareURL for why the client must not derive it.
  storyShareURL = data.share_url || '';
  track('story.viewed', { story_id: id, position: pos ?? -1, source: 'catalog' });

  // Lore has its own page. The story carries a link, so nothing about the reference material is
  // fetched, parsed or rendered for a reader who only came to read the chapter.
  const ll = el('lore-link');
  if (ll) ll.href = `#/lore/${encodeURIComponent(id)}`;

  el('story-cover').src = coverURL(id);
  el('story-cover').alt = `Cover of ${story.title}`;
  el('story-title').textContent = story.title;
  el('story-premise').textContent = story.tagline || '';
  el('story-labels').textContent = [story.region, ...(story.genres || [])].filter(Boolean).map(label).join(' · ');

  renderFormats();

  let resume = 0;
  if (token) {
    resume = await api(`/api/stories/${encodeURIComponent(id)}/progress`).then((p) => p.no).catch(() => 0);
  }
  const first = story.chapters[0]?.no ?? 1;
  el('read-btn').textContent = resume ? `Continue from chapter ${resume}` : 'Start reading';
  el('read-btn').onclick = () => go(`#/read/${id}/${resume || first}`);

  loadLikes(id);
  loadSaved(id);
  wireStoryShare(id);
  loadCommentCount(id);

  renderArcs(data.arcs, resume);

  loreCache = null;
  // NO TAB FOR AN EMPTY ROOM. An absent option must not be offered -- the same rule the narrator
  // switcher follows. A Watch tab that opens on "No chapters are unlocked yet" tells a reader the
  // paywall stopped them when the truth is that nothing was ever rendered, which is a different
  // sentence and an untrue one.
  const wt = el('tab-watch');
  if (wt) wt.hidden = narrated.length === 0;
  // Chapters unless the dock asked to come home. Consumed here so a later plain visit to the story
  // is not still honouring a request made three navigations ago.
  const watchFirst = wantWatchTab && narrated.length > 0;
  wantWatchTab = false;
  showTab(watchFirst ? 'watch' : 'chapters');
  const lockedOf = new Map(access.map((a) => [a.no, a.locked]));
  el('chapters').innerHTML = story.chapters
    .map((c) => {
      const locked = lockedOf.get(c.no);
      return `<li><button class="chap" data-chapter="${c.no}" ${locked ? 'data-locked' : ''}>
        <span class="chap__no">${String(c.no).padStart(2, '0')}</span>
        <span class="chap__title">${esc(c.title)}</span>
        ${locked ? '<span class="chap__lock">Locked</span>' : ''}
      </button></li>`;
    })
    .join('');
}

// Only editions that exist FOR THIS CHAPTER are offered — a control that promises something the
// product cannot deliver is the failure this whole slice exists to correct. Video is therefore
// conditional: 275 narrated cuts exist but they do not cover the library, so the button appears
// only when the API has said this chapter has one (`content.video`). Manga and Novel are always
// offered because every chapter has prose and the manga control degrades visibly via reader-note.
// Both the story page and the reader carry the control, because the choice is most often made
// mid-chapter — a reader hits an undrawn chapter and wants the prose right there, not after
// navigating back to the story.
// LIKES. The count is public, so it is fetched and shown for signed-out visitors too; only the
// press needs a session. A visitor who is not signed in gets the number and a control that says so
// when pressed, rather than a hidden button that makes the story look unliked.
async function loadLikes(id) {
  const btn = el('like-btn');
  if (!btn) return;
  btn.hidden = false;
  btn.disabled = true;
  try {
    const r = await api(`/api/stories/${encodeURIComponent(id)}/likes`);
    paintLikes(r);
  } catch {
    // A catalogue that cannot count is still a catalogue: hide the control rather than show a
    // zero that is not true.
    btn.hidden = true;
    return;
  }
  btn.disabled = false;
  btn.onclick = () => toggleLike(id);
}

function paintLikes({ likes = 0, liked = false }) {
  const btn = el('like-btn');
  if (!btn) return;
  btn.setAttribute('aria-pressed', liked ? 'true' : 'false');
  el('like-count').textContent = likes;
  el('like-label').textContent = liked ? 'Liked' : 'Like';
  btn.setAttribute('aria-label', `${likes} ${likes === 1 ? 'like' : 'likes'}. ${liked ? 'Unlike' : 'Like'} this story.`);
}

async function toggleLike(id) {
  if (!token) {
    go('#/account');
    return;
  }
  const btn = el('like-btn');
  const liked = btn.getAttribute('aria-pressed') === 'true';
  // Paint the new state first so the tap lands immediately, then reconcile with whatever the
  // server actually counted -- the server's number is the truth, including other readers' likes
  // that arrived while this page was open.
  paintLikes({ likes: Math.max(0, Number(el('like-count').textContent || 0) + (liked ? -1 : 1)), liked: !liked });
  btn.disabled = true;
  try {
    const r = await api(`/api/stories/${encodeURIComponent(id)}/like`, { method: liked ? 'DELETE' : 'POST' });
    paintLikes(r);
    track('story.liked', { story_id: id, liked: !liked });
  } catch {
    // Put the optimistic paint back the way it was; the press did not happen.
    paintLikes({ likes: Math.max(0, Number(el('like-count').textContent || 0) + (liked ? 1 : -1)), liked });
  }
  btn.disabled = false;
}

// SHARE. The only control on this page that brings a reader who is not already here.
//
// WHAT IT SHARES IS NOT WHAT THE READER IS LOOKING AT. The app is a hash-routed single page, so
// location.href is #/story/x -- an address with no OG card, because the card lives on the
// server-rendered /story/<slug>. Sharing the browser's URL would send a link that previews as the
// site's generic card, or as nothing. The server hands over the right string; this shares it.
//
// navigator.share is the native sheet on mobile, which is where sharing actually happens. It is
// absent on most desktop browsers and it REJECTS when the reader dismisses the sheet, so a
// dismissal must not look like a failure -- hence the AbortError check. Clipboard is the fallback
// and also the desktop path.
// WHERE A READER CAN ACTUALLY SEND A LINK.
//
// INSTAGRAM IS NOT ON THIS LIST AND CANNOT BE. It has no web share URL — no documented scheme
// takes a link from a browser into a post or a story. The only route to it is the operating
// system's own sheet, which is the first item below on any device that has one, and Instagram
// appears there when the app is installed. A button labelled "Instagram" that silently opened a
// profile page, or copied to the clipboard and hoped, would be a lie told in the UI.
//
// The rest are plain documented intent URLs, so they need no SDK, no App ID and no third-party
// script — which also means none of them can track this page's readers before they choose to go.
const SHARE_TARGETS = [
  { id: 'whatsapp', label: 'WhatsApp',
    href: (u, t) => `https://wa.me/?text=${encodeURIComponent(t + ' ' + u)}` },
  { id: 'x', label: 'X',
    href: (u, t) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
  { id: 'facebook', label: 'Facebook',
    href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { id: 'reddit', label: 'Reddit',
    href: (u, t) => `https://www.reddit.com/submit?url=${encodeURIComponent(u)}&title=${encodeURIComponent(t)}` },
  { id: 'telegram', label: 'Telegram',
    href: (u, t) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
  { id: 'email', label: 'Email',
    href: (u, t) => `mailto:?subject=${encodeURIComponent(t)}&body=${encodeURIComponent(u)}` },
];

// SHARING EARNS A COIN, AND IT IS CLAIMED WHERE A SHARE IS ALREADY *COUNTED*.
//
// The three share paths below already distinguish a real share from a dismissed one -- nativeShare
// checks for AbortError precisely because navigator.share rejects when the reader closes the sheet,
// and a dismissal must not look like a share. Hooking the reward to the same moment as track()
// inherits that judgement instead of making it a second time and getting it slightly different.
//
// The server is the authority on whether it pays: the key is share:<story>:<day>, so a second
// share of the same story today earns nothing, and the global daily cap applies on top. The client
// does not predict either -- it reports what the server actually granted.
async function claimShareReward(props) {
  const id = props && props.story_id;
  if (!token || !id) return;
  try {
    const out = await api('/api/me/rewards/share', {
      method: 'POST', body: JSON.stringify({ story_id: id }),
    });
    // 0 is the normal answer for an already-shared story or a spent allowance, and saying
    // "you earned nothing" would make a successful share feel like a failure.
    if (out && out.credit_earned > 0) toast(`+${out.credit_earned} coin for sharing`);
  } catch {
    // A reward that could not be recorded must never make a successful share look failed. The
    // reader shared; that worked. Tomorrow's coin is recoverable, a false error is not.
  }
}

// shareTo opens one target and records WHICH one, because "share" as a single number cannot tell
// you where readers actually are.
function shareTo(target, url, title, event, props) {
  window.open(target.href(url, title), '_blank', 'noopener,noreferrer');
  track(event, { ...props, method: target.id });
  claimShareReward(props);
}

async function copyShareLink(url, event, props) {
  try {
    await navigator.clipboard.writeText(url);
    toast('Link copied');
    track(event, { ...props, method: 'clipboard' });
    claimShareReward(props);
  } catch {
    // Clipboard can be refused by permission policy. Show the URL so the reader can copy it by
    // hand rather than leaving them with a control that did nothing.
    toast(url);
    track(event, { ...props, method: 'manual' });
  }
}

// nativeShare is the OS sheet: every installed app, Instagram included. Absent on most desktops.
async function nativeShare(url, title, event, props) {
  try {
    await navigator.share({ title, url });
    track(event, { ...props, method: 'native' });
    claimShareReward(props);
  } catch (err) {
    // A dismissed sheet rejects and is not a failure, so it is not counted as a share either.
    if (!err || err.name !== 'AbortError') toast('Could not open the share sheet');
  }
}

// fillSharePanel renders the menu for one link. Called on open rather than at page load, because
// the chapter share's URL is not known until the chapter is.
function fillSharePanel(panel, url, title, event, props) {
  if (!panel || !url) return;
  panel.innerHTML = '';
  const add = (label, onClick) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'share__target';
    b.textContent = label;
    b.onclick = () => { onClick(); panel.closest('details').open = false; };
    panel.appendChild(b);
  };
  // The OS sheet first where it exists: it is the shortest path and the only one that reaches
  // Instagram, so burying it under the web targets would be the wrong order.
  if (navigator.share) add('More apps…', () => nativeShare(url, title, event, props));
  for (const t of SHARE_TARGETS) add(t.label, () => shareTo(t, url, title, event, props));
  add('Copy link', () => copyShareLink(url, event, props));
}

function wireStoryShare(id) {
  const menu = el('share-menu');
  if (!menu) return;
  menu.hidden = !storyShareURL;
  menu.ontoggle = () => {
    if (menu.open) {
      fillSharePanel(el('share-panel'), storyShareURL,
        story ? story.title : 'Sagapic', 'story.shared', { story_id: id });
    }
  };
}

function renderFormats() {
  const editions = [['manga', 'Manga'], ['novel', 'Novel']];
  if (chapter?.video) editions.push(['video', 'Narrated']);
  const html = editions
    .map(([id, name]) => `<button data-edition="${id}" aria-pressed="${edition === id}">${name}</button>`)
    .join('');
  document.querySelectorAll('[data-formats]').forEach((n) => { n.innerHTML = html; });
}

function setEdition(next) {
  if (next === edition) return;
  edition = next;
  localStorage.setItem(EDITION_KEY, edition);
  renderFormats();
  track('format.chosen', { story_id: story?.id, format: edition });
  if (!el('view-reader').hidden && chapter) renderChapter();
}

// ── story tabs: chapters · lore · watch ────────────────────────────────────
//
// The API served all three and the site showed only the first. 54 stories have an authored lore
// file and 281 narrated videos exist on disk; both were reachable only by opening the repository.
let activeTab = 'chapters';

function showTab(name) {
  activeTab = name;
  document.querySelectorAll('.tab').forEach((t) =>
    t.setAttribute('aria-selected', String(t.dataset.tab === name)));
  document.querySelectorAll('[data-panel]').forEach((n) => {
    n.hidden = n.dataset.panel !== name;
  });
  if (name === 'watch') renderWatch();
  if (name === 'comments') renderComments();
  // Leaving the Watch tab is the commonest way to take a playing narration off-screen.
  updatePlayerPlacement();
}

// Rendered from whatever sections the file HAPPENS to contain. The server returns lore verbatim
// because its shape differs between stories and keeps changing; the client must therefore render
// what is there rather than named fields it expects.
let loreCache = null;
async function loadLore() {
  const box = el('lore');
  if (loreCache && loreCache.id === story.id) return;
  box.innerHTML = '<p class="empty">Loading…</p>';
  try {
    const d = await api(`/api/stories/${encodeURIComponent(story.id)}/lore`);
    loreCache = { id: story.id, lore: d.lore };
    box.innerHTML = renderLore(d.lore) ||
      '<p class="empty">No lore has been written for this story yet.</p>';
  } catch (e) {
    // 404 is the NORMAL case — only 54 of 114 stories have a lore file — so it must not read as
    // an error.
    box.innerHTML = e.status === 404
      ? '<p class="empty">No lore has been written for this story yet.</p>'
      : `<p class="empty">Lore could not be loaded. ${esc(e.message)}</p>`;
  }
}

const TITLES = { world_id: 'World', world_connection: 'Connection', setting: 'Setting',
  characters: 'Characters', timeline: 'Timeline', themes: 'Themes', faq: 'Questions' };

function renderLore(lore) {
  if (!lore || typeof lore !== 'object') return '';
  return Object.entries(lore).map(([key, val]) => {
    const title = TITLES[key] || key.replace(/_/g, ' ');
    return `<section><h3>${esc(title)}</h3>${loreValue(val)}</section>`;
  }).join('');
}

// Field names a reader should see, rather than the JSON key.
const LORE_FIELD = { role: 'Role', appearance: 'Appearance', personality: 'Personality',
  backstory: 'Backstory', motivations: 'Motivations', arc: 'Arc', abilities: 'Abilities',
  relationships: 'Relationships', dynamic: 'Dynamic', a: 'Answer', event: 'Event',
  overview: 'Overview', quotes: 'Quotes', notes: 'Notes' };
function loreLabel(k) {
  return LORE_FIELD[k] || k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

// Craft vocabulary a READER should never be shown. "Deuteragonist" is a term from Greek drama and
// "Escalating antagonist tier" is a note to a writers' room; both were being printed on the page.
// Mirrors reader_role() in scripts/build-site.py — the same fix had already been made on the static
// site, which is not the one anybody visits.
const CRAFT_ROLE = /^(?:the\s+)?(?:first|second|third|primary|secondary|main|apparent|escalating|final)?\s*(?:protagonist|deuteragonist|tritagonist|antagonist(?:\s+tier)?|ally|rival|mentor|supporting|minor|male\s+lead|female\s+lead|co-protagonist|love\s+interest|foil)\s*$/i;
function readerRole(role) {
  const r = (role || '').trim();
  if (CRAFT_ROLE.test(r)) return '';
  for (const sep of ['\u2014', ';', '.']) {
    const i = r.indexOf(sep);
    if (i > 0 && CRAFT_ROLE.test(r.slice(0, i).trim())) {
      const tail = r.slice(i + 1).trim();
      return tail ? tail[0].toUpperCase() + tail.slice(1) : '';
    }
  }
  return r;
}

function loreValue(v) {
  if (v == null) return '';
  if (typeof v === 'string') return `<p>${esc(v)}</p>`;
  if (Array.isArray(v)) {
    // An array of objects is a list of NAMED things — characters, timeline entries, questions —
    // so it renders as a definition list rather than losing the label in a bullet.
    if (v.length && typeof v[0] === 'object') {
      // THE HEADING WAS THE DATABASE KEY. Object.values()[0] is `id`, so a character rendered as
      // "meixiang — Mei Xiang — Protagonist — <appearance> — <personality>": a raw slug followed by
      // every field mashed together with dashes. The owner asked for character and world REFERENCE,
      // in the sense a fandom wiki means it, and this was the opposite of that.
      //
      // The term is now the thing's NAME (or its question, or its date), the fields are labelled,
      // and `id` never appears — it is addressing, not content.
      return `<dl class="lore__list">${v.map((o) => {
        const name = o.name || o.q || o.when || o.title || o.with || '';
        const body = Object.entries(o)
          .filter(([k, x]) => k !== 'id' && x
            && x !== name && k !== 'name' && k !== 'q' && k !== 'when' && k !== 'title' && k !== 'with')
          .map(([k, x]) => `<div class="lore__field"><span class="lore__k">${esc(loreLabel(k))}</span>`
            + `<span class="lore__v">${
              typeof x === 'string' ? esc(k === 'role' ? readerRole(x) : x) : loreValue(x)
            }</span></div>`)
          .join('');
        return `<dt>${esc(name)}</dt><dd>${body}</dd>`;
      }).join('')}</dl>`;
    }
    return `<ul>${v.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>`;
  }
  // RECURSE, DO NOT STRINGIFY. `setting` is an object whose values are often lists of objects —
  // setting.locations, and a top-level glossary in two stories — and JSON.stringify put raw JSON on
  // the page. That is what the owner saw: "why is there a json of glossary etc". A nested value is
  // rendered by the same function that renders a top-level one.
  return `<dl class="lore__list">${Object.entries(v).map(([k, x]) =>
    `<dt>${esc(loreLabel(k))}</dt><dd>${
      typeof x === 'string' ? `<p>${esc(x)}</p>` : loreValue(x)
    }</dd>`
  ).join('')}</dl>`;
}

// WATCH — the narrated videos built from the manga panels. This is where the pipeline's output is
// actually verifiable: the panels and the narration together, as a reader would receive them.
// Only unlocked chapters are offered; a locked one would 402 into a broken player.
const AUTOPLAY_KEY = 'aic.autoplay';
let autoplay = localStorage.getItem(AUTOPLAY_KEY) !== '0';   // on by default: this is a binge format

// THE QUEUE LISTED CHAPTERS THAT CANNOT PLAY, AND IT WAS REPORTED FROM THE LIVE SITE.
//
// The owner: "in some stories, i can see a list but nothing opens". This filtered on the PAYWALL
// alone -- unlocked meant watchable -- and unlocked has nothing to do with whether a video was
// ever rendered. Measured across the live catalogue: 34 of 151 stories have no narrated cut for any
// chapter and showed a full queue over a player whose src 404s, and the commoner case is a story
// with chapter 1 narrated and nothing after it, offering fifteen episodes of which one plays.
//
// Two conditions, and BOTH are required: the paywall must allow it and the cut must exist. `narrated`
// is the server's answer to the second; the client has no way to see the media tree and must not
// pretend otherwise.
function watchable() {
  const lockedOf = new Map(access.map((a) => [a.no, a.locked]));
  const plays = new Set(narrated);
  return (story.chapters || []).filter((c) => !lockedOf.get(c.no) && plays.has(c.no));
}

// NARRATOR CHOICE, remembered per story.
//
// Stored per story rather than globally because the owner's requirement is explicit: a reader may
// set a preference for ONE story without changing the rest, and a story-level choice beats the
// site default. Kept client-side for now; when signed in this is what the server-side per-user,
// per-story preference will mirror.
const NARRATOR_KEY = 'aic.narrator';
let narratorPrefs = {};
try { narratorPrefs = JSON.parse(localStorage.getItem(NARRATOR_KEY) || '{}') || {}; } catch { narratorPrefs = {}; }

function narratorFor(storyId) {
  return narratorPrefs[storyId] || 'female';
}

function setNarrator(storyId, who) {
  narratorPrefs[storyId] = who;
  try { localStorage.setItem(NARRATOR_KEY, JSON.stringify(narratorPrefs)); } catch { /* private mode */ }
}

// THE STORY BEING PLAYED IS NOT ALWAYS THE STORY ON SCREEN, and reading the global `story` here
// was a bug with teeth. Measured in a browser: with a narration docked and the reader browsing a
// DIFFERENT story, pressing the dock's forward button built a URL from the story on screen — so it
// loaded chapter 2 of whatever the reader happened to be looking at, while the label beside it
// still read the correct title from the player's own queue. Wrong video, right caption.
//
// Defaults to the player's story, so no caller is obliged to remember; renderWatch passes the
// story it is rendering, which is the one case where the two differ legitimately.
const videoSrc = (no, storyId) => {
  const sid = storyId || playerStory || (story && story.id) || '';
  return `${API}/media/stories/${encodeURIComponent(sid)}/chapters/${no}/video`
    + `?narrator=${encodeURIComponent(narratorFor(sid))}`;
};

function renderWatch() {
  // PARKED BEFORE THE PANEL IS REBUILT, AND THIS IS NOT OPTIONAL.
  //
  // The two lines below replace #watch's innerHTML, which destroys #player-slot and ANYTHING
  // INSIDE IT — including the player, once it had been moved in. Measured in a browser: the second
  // visit to the Watch tab removed the element from the document, every later el('reel') returned
  // null, and the next click threw "Cannot read properties of null". Moving it to the body first
  // keeps the node alive and playing; the slot is re-adopted at the end of this function.
  parkPlayer();
  const open = watchable();
  if (!open.length) {
    // Reached only when cuts EXIST and the paywall holds every one of them -- the tab is hidden
    // outright when none exist, so this no longer has to cover both and mis-describe one.
    el('watch').innerHTML = '<p class="empty">The narrated chapters of this story are part of '
      + 'the subscription.</p>';
    return;
  }
  // ONE PLAYER, NOT A GRID OF THEM, AND IT IS NOT BUILT HERE ANY MORE.
  //
  // A wall of separate <video> elements cannot give a continuous feeling — each is its own
  // beginning, and the reader has to hunt for the next one. A single player with a queue beside it
  // is how a serialized format is actually watched.
  //
  // The player element itself lives in the document body and is MOVED into this slot, because
  // building it here destroyed it: this function runs on every tab switch, innerHTML replaced the
  // markup, and a chapter the reader was part-way through was thrown away. See #player in
  // index.html for why it also has to survive leaving the page entirely.
  el('watch').innerHTML = `
    <div id="player-slot"></div>
    <ol class="queue" id="queue">${open.map((c, i) => `
      <li><button class="q" data-video="${c.no}" aria-current="${playerStory === story.id && c.no === currentNo}">
        <span class="q__no">${String(c.no).padStart(2, '0')}</span>
        <span class="q__t">${esc(c.title)}</span>
      </button></li>`).join('')}</ol>`;

  // ALREADY PLAYING THIS STORY: adopt it where it is rather than starting again. A reader who
  // wandered off to the catalogue and came back is mid-chapter, and restarting from the top of the
  // queue would throw that away — which is the same mistake as destroying the element, arrived at
  // politely.
  if (playerStory === story.id && currentNo != null) {
    playerQueue = open;
    placePlayer('inline');
    return;
  }
  playerStory = story.id;
  playerQueue = open;
  placePlayer('inline');
  el('reel').poster = coverURL(story.id);
  el('reel').src = videoSrc(open[0].no, story.id);
  el('reel-now').textContent = `${String(open[0].no).padStart(2, '0')} · ${esc(open[0].title)}`;
  queueNext(open, 0);
  loadNarrators(open[0].no);
  // AUTOPLAY, AND WHAT BROWSERS ACTUALLY ALLOW.
  //
  // The setting used to mean only "advance when one ends" — the first video never started itself,
  // so "play continuously" did nothing until the reader pressed play.
  //
  // The other half is policy: every current browser refuses UNMUTED autoplay until the user has
  // interacted with the site. Muting is not an option here — the whole artefact is a narration, and
  // a silent one is worse than a paused one. So we attempt sound-on playback and, if the browser
  // refuses, say so plainly and leave the reader one obvious control, rather than showing a poster
  // that looks broken. After they press play once, the permission sticks for the session and every
  // later chapter starts on its own.
  if (autoplay) tryPlay(el('reel'));
}

// WHICH STORY THE PLAYER BELONGS TO. Without it, opening a DIFFERENT story while one is playing
// would let that story's Watch tab adopt the running chapter and label it with its own titles.
let playerStory = null;
// AND ITS OWN QUEUE, NOT THE ONE ON SCREEN.
//
// stepChapter first read watchable(), which is derived from the global `story` — so measured in a
// browser, opening a second story while a narration was docked made the forward and back buttons
// SILENTLY DO NOTHING: the guard `story.id === playerStory` failed and the click was dropped. A
// control that is visible, enabled and inert is the same defect as the dead Post button, arrived at
// from the other direction. The queue is captured when the player claims a story and travels with
// it.
let playerQueue = [];
// The Watch tab a route change should land on, set by the dock's own way back. loadStory otherwise
// always opens Chapters, so "Back to story" reached the right story and left the player docked
// behind the wrong tab.
let wantWatchTab = false;

// playerEl is held as a NODE, not looked up by id each time.
//
// getElementById cannot find an element that is not in the document, and this one is legitimately
// out of it for the instant between being parked and re-adopted. Holding the reference means a
// lookup during that window returns the player rather than null.
const playerEl = el('player');

// parkPlayer moves the player somewhere nothing is about to re-render, keeping it playing.
function parkPlayer() {
  if (playerEl && playerEl.parentNode !== document.body) document.body.appendChild(playerEl);
}

// placePlayer moves the one player element between the Watch panel and the floating dock.
//
// 'inline'  — in the Watch panel, full size, no dock furniture.
// 'dock'    — fixed to the corner of whatever page the reader moved to, still playing, with
//             controls, a way back to the story and a way to stop.
// 'off'     — hidden and paused. The only thing that stops playback.
function placePlayer(mode) {
  const p = playerEl;
  if (!p) return;
  if (mode === 'off') {
    // PAUSE EXPLICITLY. Hiding it is what caused the reported bug in the first place.
    el('reel').pause();
    p.hidden = true;
    p.classList.remove('player--dock');
    playerStory = null;
    currentNo = null;
    return;
  }
  const slot = el('player-slot');
  const target = mode === 'inline' && slot ? slot : document.body;
  // appendChild on an element already in the right place is a no-op move, and moving it is safe
  // even mid-playback: see the note on #player in index.html.
  if (p.parentNode !== target) target.appendChild(p);
  p.hidden = false;
  p.classList.toggle('player--dock', mode !== 'inline');
  el('reel-open').hidden = mode === 'inline';
}

// updatePlayerPlacement decides where the player belongs after any navigation.
//
// Called from show() and showTab() — every route change goes through one of them — so there is no
// path that can leave a playing video off-screen and uncontrollable, which is the bug this exists
// to make impossible rather than merely fixed.
function updatePlayerPlacement() {
  const p = playerEl;
  if (!p || p.hidden || !playerStory) return;
  const onItsOwnWatchTab = !el('view-story').hidden && activeTab === 'watch'
    && story && story.id === playerStory;
  // NOT PAUSED ON NAVIGATION, DELIBERATELY. The owner's requirement is that it "keep on playing";
  // a reader who wants it to stop has Stop, and the native pause control travels with it.
  placePlayer(onItsOwnWatchTab ? 'inline' : 'dock');
}

let currentNo = null;

// Registered ONCE, against elements that now outlive every render. They used to be re-bound on
// each renderWatch() because the markup was rebuilt each time; against a persistent player that
// would stack a new handler on every tab switch.
el('autoplay').checked = autoplay;
el('autoplay').onchange = (e) => {
  autoplay = e.target.checked;
  localStorage.setItem(AUTOPLAY_KEY, autoplay ? '1' : '0');
};
el('reel').onended = () => {
  if (!el('autoplay').checked) return;
  stepChapter(1);
};
// "move ahead etc" — the owner asked for these by name. Native controls seek WITHIN a chapter;
// these move between them, which is the unit this format is actually watched in.
el('reel-fwd').onclick = () => stepChapter(1);
el('reel-prev').onclick = () => stepChapter(-1);
el('reel-close').onclick = () => placePlayer('off');
// Back to the story the narration belongs to — which is not necessarily the page the reader is on.
el('reel-open').onclick = () => {
  if (!playerStory) return;
  // The WATCH tab, not the story's default. Landing on Chapters left the player docked in the
  // corner of the very page it belongs to, which is the one place it should be full size.
  wantWatchTab = true;
  go(`#/story/${playerStory}`);
  // Already on that story: the hash does not change, so route() never runs and nothing would
  // happen at all.
  if (story && story.id === playerStory) showTab('watch');
};

// stepChapter advances or rewinds by one PLAYABLE chapter.
//
// Reads the queue for the story the PLAYER belongs to, not the story on screen: while docked the
// reader may be looking at something else entirely, and the forward button must still mean "next
// chapter of what I am listening to".
function stepChapter(delta) {
  const open = playerQueue;
  if (!open.length || !playerStory) return;
  const i = open.findIndex((c) => c.no === currentNo);
  const next = i >= 0 ? i + delta : 0;
  if (next < 0 || next >= open.length) return;
  playChapter(open, next, playerStory);
}

function queueNext(open, i, storyId) {
  currentNo = open[i].no;
  const next = open[i + 1];
  const pre = el('reel-next');
  const sid = storyId || playerStory;
  // Warming the NEXT one only. Preloading the whole queue would pull tens of megabytes the reader
  // may never watch, which on a phone is their data and their battery.
  if (pre) pre.src = next ? videoSrc(next.no, sid) : '';
}

// tryPlay attempts sound-on playback and surfaces a refusal instead of swallowing it.
function tryPlay(v) {
  // Counted at the attempt rather than on the 'playing' event: a refusal is itself a finding, and
  // the catch below already distinguishes it.
  const [, , sid, no] = location.hash.split('/');
  ga('episode_play', { story_id: sid || '', episode: Number(no) || 0 });
  const p = v.play();
  if (!p || !p.catch) return;
  p.catch(() => {
    const bar = el('reel-now');
    if (bar && !bar.dataset.blocked) {
      bar.dataset.blocked = '1';
      // Named for what the reader must do, not for what the browser did. "NotAllowedError" is
      // true and useless; "press play once" is actionable and only has to be done once.
      bar.textContent = 'Press play to start — your browser blocks sound until you do.';
    }
  });
}

// `owner` is the story the queue belongs to. It is an ARGUMENT rather than a read of the global
// `story` because the dock plays on while the reader browses elsewhere: taking it from the page
// meant the forward button reassigned the player to whatever was on screen and then loaded that
// story's chapter instead.
function playChapter(open, i, owner) {
  const c = open[i];
  const v = el('reel');
  const sid = owner || playerStory;
  playerStory = sid;
  playerQueue = open;
  v.src = videoSrc(c.no, sid);
  tryPlay(v);
  el('reel-now').textContent = `${String(c.no).padStart(2, '0')} · ${esc(c.title)}`;
  document.querySelectorAll('[data-video]').forEach((b) =>
    b.setAttribute('aria-current', String(Number(b.dataset.video) === c.no)));
  queueNext(open, i, sid);
  // Refresh the switcher for the NEW chapter. Chapters are narrated independently, so the voices
  // available change per chapter — without this the control kept whatever the first chapter had,
  // which meant it stayed hidden for a chapter that did have both cuts.
  loadNarrators(c.no, sid);
  track('chapter.opened', { story_id: sid, chapter_no: c.no, format: 'video' });
  saveProgress(sid, c.no);
}

// ── reader ─────────────────────────────────────────────────────────────────
let chapter = null;
let completed = false;

async function loadChapter(id, no) {
  el('reader-body').innerHTML = '<div class="skeleton" style="aspect-ratio:16/9"></div>';
  el('reader-end').hidden = true;
  completed = false;
  window.scrollTo(0, 0);

  try {
    chapter = await api(`/api/stories/${encodeURIComponent(id)}/chapters/${no}/content`);
  } catch (e) {
    if (e.status === 402) {
      track('paywall.shown', { story_id: id, chapter_no: Number(no) });
      // THE READER BEHIND THE PAYWALL WAS SHOWING THE LAST CHAPTER'S HEADER OVER A SKELETON.
      // renderChapter() sets the eyebrow and it never runs on this path, so the header kept
      // whatever was rendered last -- measured in a browser at #/read/emberline/40 arriving from
      // another story: the URL said emberline, the header said "Blackline · Chapter 1", and
      // reader-body held a skeleton that would never resolve. Dismissing the paywall with "Not
      // now" -- the commonest thing a non-subscriber does -- left them looking at another story's
      // title over an empty page.
      // `story` is correct here: route() loads it before this runs. Only the paint was stale.
      chapter = null;
      el('reader-eyebrow').textContent = `${story?.title || ''} · Chapter ${no}`;
      el('reader-body').innerHTML =
        '<p class="narr">This chapter is part of the subscription. '
        + 'The first chapters of every story stay free.</p>';
      openPaywall(id, no);
      return;
    }
    // THE SAME STALE HEADER AS THE 402 BRANCH ABOVE, and fixing only that one left this one.
    // renderChapter() sets the eyebrow and does not run on any error path, so #/read/<story>/99
    // showed "Blackline · Chapter 1" over "chapter not found" -- a stale link or a mistyped
    // number reports the wrong chapter while telling the reader it is missing.
    chapter = null;
    el('reader-eyebrow').textContent = `${story?.title || ''} · Chapter ${no}`;
    // e.message is the API's own wording ("chapter not found"), which reads as a fragment when
    // appended to a sentence. The reader is told what happened, not what the server called it.
    el('reader-body').innerHTML =
      '<p class="narr">This chapter could not be loaded. It may have moved, or the link may be '
      + 'out of date.</p>';
    return;
  }

  // The edition control is per-chapter now that Narrated is conditional, so it has to be rebuilt
  // on every chapter open — not just when the story page loads.
  renderFormats();
  renderChapter();
  paintDisclosure();
  el('reader-end').hidden = false;
  const shareMenu = el('share-chapter-menu');
  if (shareMenu) {
    shareMenu.hidden = !chapter.share_url;
    shareMenu.open = false;
    shareMenu.ontoggle = () => {
      if (shareMenu.open) {
        fillSharePanel(el('share-chapter-panel'), chapter.share_url,
          `${story ? story.title : 'Sagapic'} — chapter ${chapter.no}`,
          'chapter.shared', { story_id: id, chapter_no: Number(chapter.no) });
      }
    };
  }
  wireChapterComments(id, no);
  renderChapterComments(id, no);
  el('next-btn').hidden = !chapter.next;
  el('next-btn').onclick = () => go(`#/read/${id}/${chapter.next}`);
  track('chapter.opened', {
    story_id: id, chapter_no: chapter.no, rendered: chapter.rendered, format: shownEdition(),
  });
  saveProgress(id, chapter.no);
  updateTrail();
}

// shownEdition is what the reader will actually SEE, which is not always what they chose: a
// chapter with no panels has no manga edition. Falling back silently is right — the alternative
// is an empty page or a modal explaining an absence — but the fallback must be visible in the
// analytics, or "manga" would appear to be selected for chapters that never had it.
function shownEdition() {
  // Video falls back the same way manga does, and for the same reason: the cut is missing far
  // more often than it is present, and an empty player is worse than the prose.
  if (edition === 'video') return chapter?.video ? 'video' : 'novel';
  return chapter?.rendered ? edition : 'novel';
}

function renderChapter() {
  const showing = shownEdition();
  el('reader-eyebrow').textContent = `${story?.title || ''} · Chapter ${chapter.no}`;
  el('reader-title').textContent = chapter.title;
  el('reader-note').hidden = !(chapter.rendered === false && edition === 'manga');

  if (showing === 'video') {
    // preload="none" is deliberate: these cuts run to ~12 MB and the reader may only be passing
    // through the chapter. The poster is the first panel when one has been drawn.
    const poster = chapter.panels.find((p) => p.image);
    el('reader-body').innerHTML =
      `<video class="chapter-video" controls playsinline preload="none"` +
      (poster ? ` poster="${API}${poster.image}"` : '') +
      ` src="${API}/media/stories/${chapter.story_id}/chapters/${chapter.no}/video"></video>`;
    updateTrail();
    return;
  }

  // A chapter starts with nobody having spoken yet; carrying a speaker in from the last chapter
  // read would attribute its first "you" to someone who is not in the room.
  lastSpeaker = null;
  el('reader-body').innerHTML = chapter.panels
    .map((p) => {
      if (showing === 'manga') {
        if (!p.image) return '';
        const art = `<img class="panel" src="${API}${p.image}" alt="Panel ${p.index}" loading="lazy" decoding="async">`;
        // Un-lettered art carries no words at all, so the text has to go somewhere or the panel
        // is mute. Lettered art already has it.
        return chapter.lettered ? art : art + prose(p, false);
      }
      return prose(p, true);
    })
    .join('');
  updateTrail();
}

// prose renders a panel as words. `staged` includes the scene direction, which is what gives the
// novel edition its setting between lines of dialogue — without it a chapter reads as a
// transcript. It is omitted beside an image, where the picture IS the staging.
// THIS WAS A SCREENPLAY AND THE OWNER SAID SO TWICE.
//
// It rendered the raw scene as a stage direction — camera cues and all, "two-shot", "wide shot" —
// then every line of dialogue as its own paragraph with the speaker's name in bold. Stage
// direction, character name, line. That is a script, not a novel, and it is what a reader of the
// prose edition on sagapic.com has been getting.
//
// worker/prose.py already solved this for the video and the static site, and none of that reached
// here: the live prose edition is built in this file from the raw panel JSON. Sixth time in this
// session a correct fix sat in a place nobody visits. This is the same weave, in JS.
//
//   the scene becomes a past-tense SENTENCE, camera vocabulary stripped
//   dialogue becomes reported speech INSIDE the paragraph, not a labelled line
//   attribution is dropped when the same person keeps talking, as prose does
const SHOT_TAIL = /[;,]\s*(?:[a-z-]+\s+)?(?:two-shot|three-shot|wide shot|medium shot|close[- ]?up|overhead shot|insert shot|establishing shot|low-angle|high-angle|shot|close on the [^;,]*)\s*$/i;
// A clause that OPENS with camera vocabulary is a shot cue, not a beat, and is skipped so the
// framer reaches the sentence after it. Kept identical to _SHOT_HEAD in worker/prose.py: this
// list was missing `extreme`, `top-down`, `closeup` and `detail`, so "Extreme close two-shot:
// Ha-eun's eyes flutter open. She grips Do-yun's collar." framed nothing on the page while the
// voiceover said "She gripped Do-yun's collar."
const SHOT_HEAD = /^(two-shot|wide|close|closeup|close-up|medium|overhead|top-down|insert|macro|low-angle|high-angle|extreme|establishing|detail|still)\b/i;
const SHOT_LEAD = /^\s*(?:[a-z-]+\s+)?(?:two-shot|three-shot|shot|close[- ]?up|medium shot|wide shot|overhead shot)(?:\s+(?:of|on|at)\s+[^,:;]*)?\s*[:,]\s*/i;
// Irregular pasts, kept IDENTICAL to prose.py's _IRREGULAR_PAST. The browser's copy had 86 of
// the narrator's 133, so the page read "breaked", "eated", "forgetted", "shooted" and
// "freezed" while the voiceover said them correctly. Nothing detected that for weeks --
// worker/tests/test_prose_js_parity.py now does, and it is the reason to edit both or neither.
const IRREG_PAST = {
  be:'was', become:'became', begin:'began', bend:'bent', bind:'bound', bite:'bit', bleed:'bled',
  blow:'blew', break:'broke', bring:'brought', build:'built', burn:'burnt', burst:'burst',
  buy:'bought', cast:'cast', catch:'caught', choose:'chose', cling:'clung', come:'came',
  cost:'cost', creep:'crept', cut:'cut', deal:'dealt', dig:'dug', do:'did', draw:'drew',
  drink:'drank', drive:'drove', eat:'ate', fall:'fell', feed:'fed', feel:'felt', fight:'fought',
  find:'found', flee:'fled', fling:'flung', fly:'flew', forget:'forgot', forgive:'forgave',
  freeze:'froze', get:'got', give:'gave', go:'went', grind:'ground', grow:'grew', hang:'hung',
  have:'had', hear:'heard', hide:'hid', hit:'hit', hold:'held', hurt:'hurt', keep:'kept',
  kneel:'knelt', know:'knew', lay:'laid', lead:'led', lean:'leant', leap:'leapt',
  learn:'learnt', leave:'left', lend:'lent', let:'let', lie:'lay', light:'lit', lose:'lost',
  make:'made', mean:'meant', meet:'met', pay:'paid', put:'put', quit:'quit', read:'read',
  ride:'rode', ring:'rang', rise:'rose', run:'ran', say:'said', see:'saw', seek:'sought',
  sell:'sold', send:'sent', set:'set', shake:'shook', shed:'shed', shine:'shone', shoot:'shot',
  show:'showed', shrink:'shrank', shut:'shut', sing:'sang', sink:'sank', sit:'sat',
  sleep:'slept', slide:'slid', sling:'slung', slit:'slit', speak:'spoke', speed:'sped',
  spend:'spent', spill:'spilt', spin:'spun', spit:'spat', split:'split', spread:'spread',
  spring:'sprang', stand:'stood', steal:'stole', stick:'stuck', sting:'stung', stink:'stank',
  strike:'struck', string:'strung', swear:'swore', sweep:'swept', swim:'swam', swing:'swung',
  take:'took', teach:'taught', tear:'tore', tell:'told', think:'thought', throw:'threw',
  thrust:'thrust', tread:'trod', understand:'understood', wake:'woke', wear:'wore', weep:'wept',
  win:'won', wind:'wound', withdraw:'withdrew', write:'wrote',
};

function pastOf(stem) {
  if (IRREG_PAST[stem]) return IRREG_PAST[stem];
  if (stem.endsWith('e')) return stem + 'd';
  if (stem.length > 2 && stem.endsWith('y') && !'aeiou'.includes(stem[stem.length - 2])) {
    return stem.slice(0, -1) + 'ied';
  }
  const syl = (stem.match(/[aeiouy]+/g) || ['x']).length;
  const last = stem[stem.length - 1], prev = stem[stem.length - 2], prev2 = stem[stem.length - 3];
  if (syl === 1 && stem.length >= 3 && !'aeiou'.includes(last) && !'wxy'.includes(last)
      && 'aeiou'.includes(prev) && !'aeiou'.includes(prev2)) return stem + last + 'ed';
  return stem + 'ed';
}

// "goes" is go+es, "closes" is close+s, "passes" is pass+es. Stripping a fixed number of characters
// gets one of those wrong whichever number you pick.
function stemOf(verb) {
  if (!verb.endsWith('s') || verb.endsWith('ss') || verb.length < 3) return null;
  // English's irregular third-person forms: goes/does/is fall out of the rules below, `has` did
  // not — strip-one gave "ha".
  if (verb === 'has') return 'have';
  // -ies becomes -y only when something is left to carry it. "tries" is try+s, but "lies", "dies",
  // "ties" and "vies" are lie+s, die+s, tie+s, vie+s — one letter before the -ies, and the -y rule
  // gave ly/dy/ty/vy, then lyed/dyed/tyed. `dies -> dyed` is the worst: a real word, wrong meaning.
  if (verb.endsWith('ies')) return verb.length === 4 ? verb.slice(0, -1) : verb.slice(0, -3) + 'y';
  if (verb.endsWith('es')) {
    // English adds -ES only after s, x, z, ch, sh and o. Everywhere else the "e" belongs to the
    // STEM and only the "s" is the inflection: "makes" is make+s, not mak+es. Strip-two used to be
    // the fallback, which gave makked / takked / writted / commed / sed / fred to every reader.
    if (/(ss|zz|ch|sh|x|o)$/.test(verb.slice(0, -2))) return verb.slice(0, -2);
    return verb.slice(0, -1);
  }
  return verb.slice(0, -1);
}

// Move the SECOND and later verbs of a beat into the past: "and takes" -> "and took".
// Mirrors _convert_tail in worker/prose.py. Extracted from the name-led path when the article-led
// path needed the same treatment — left inline, an article-led beat came out half converted, one
// sentence in two tenses.
//
// An ADVERB may stand between the conjunction and its verb ("and gently takes"); anything else
// means the -s token is not this conjunction's verb. isAdverb answers "is this an adverb", NOT
// "could this be a verb" — stepping over a determiner turned "and her hands" into "and her handed".
function convertTail(rest) {
  let body = rest.replace(/\b(and|then)\s+([A-Z][\w'\u2019-]*(?:\s+[A-Z][\w'\u2019-]*){0,2})\s+([a-z]+s)\b/g,
    (m0, conj, name, v) => { const st = stemOf(v); return st ? `${conj} ${name} ${pastOf(st)}` : m0; });
  body = body.replace(/\b(and|then)\s+((?:[a-z]+\s+){0,3})([a-z]+s)\b/g, (m0, conj, mid, v) => {
    const words = (mid || '').trim() ? mid.trim().split(/\s+/) : [];
    if (words.some((w) => !isAdverb(w))) return m0;
    const st = stemOf(v);
    if (!st) return m0;
    const lead = words.map((w) => ` ${w}`).join('');
    return `${conj}${lead} ${pastOf(st)}`;
  });
  return body;
}

// A preposition opens a phrase the main verb cannot be inside: "A wall OF extension leads runs..."
// — `leads` is a noun there. Mirrors _PREP in worker/prose.py.
const PREP = new Set(('of in on at with from across under over through behind beside beneath '
  + 'against among amid around between before after above below near by into onto toward towards '
  + 'past along within without down up off').split(' '));
const ARTICLE_SUBJ_MAX = 5;
// How far into a beat a colon may sit and still be a prefix rather than punctuation in the clause.
const COLON_PREFIX_MAX = 70;

// An article-led beat as a past-tense sentence, or '' when the verb cannot be identified.
// Scans the -s tokens LEFT TO RIGHT and takes the first whose stem is a known head verb, rather
// than the first -s token outright: "The archive glass door bursts inward" has `glass` first.
// Mirrors _article_frame in worker/prose.py; the lexicon is the same file, emitted twice.
function articleFrame(first) {
  if (!/^(?:the|a|an)\s/i.test(first)) return '';
  const words = first.split(/\s+/);
  if (words.length < 3) return '';
  for (let i = 1; i < Math.min(words.length - 1, ARTICLE_SUBJ_MAX + 1); i += 1) {
    const low = words[i].toLowerCase().replace(/,$/, '');
    if (PREP.has(low)) return '';
    if (!low.endsWith('s') || !/^[a-z]+$/.test(low)) continue;
    const stem = stemOf(low);
    if (!stem || !HEAD_VERBS.has(stem)) continue;
    const past = pastOf(stem);
    if (!past) return '';
    const rest = convertTail(words.slice(i + 1).join(' ').replace(SHOT_TAIL, '').replace(/[\s.,]+$/, ''));
    if (!rest) return '';
    const out = `${words.slice(0, i).join(' ')} ${past} ${rest}.`;
    return out[0].toUpperCase() + out.slice(1);
  }
  return '';
}

function sceneSentence(scene) {
  const direct = frameClause(scene);
  if (direct) return direct;
  if (!scene) return '';
  // Retry ONCE past a colon prefix: "The frost-glazed east-dorm corridor: Ha-eun and Mi-ra reach
  // a locked door". The prefix is dropped rather than kept — see _scene_frame in prose.py.
  const head = scene.replace(SHOT_LEAD, '').split(/[;.]/)[0] || '';
  // An em dash is the same prefix with different punctuation: "Close-up — Tsuru knotting her sash".
  const m = head.slice(0, COLON_PREFIX_MAX + 1).match(/[:\u2014]/);
  if (!m) return '';
  return frameClause(head.slice(m.index + 1).trim());
}

function frameClause(scene) {
  if (!scene) return '';
  let t = scene.replace(SHOT_LEAD, '').split(/[;.]/).map((x) => x.trim())
    .filter((x) => x && !SHOT_HEAD.test(x))[0] || '';
  if (!t) return '';
  t = t.replace(SHOT_TAIL, '').trim();
  // ALREADY A PARTICIPLE. "Mira sawing the yard-gate chain apart" needs no conversion, only an
  // auxiliary: past PROGRESSIVE, because the participle is already there and un-doubling it back
  // to a stem is a second chance to be wrong for no gain. The weaver has done this all along and
  // this port never had it — 22 of 405 sampled beats were a sentence in the spoken edition and
  // nothing on the page.
  const done = t.match(/^((?:[A-Z][\w'\u2019-]*)(?:\s+[A-Z][\w'\u2019-]*){0,2})\s+(\w+ing)\s+(.+)$/);
  // A possessive is not a subject: "Bo's ingratiating smile" must not become "Bo's was
  // ingratiating". The name-led branch below has always guarded this; this one had not.
  if (done && /['\u2019]s$/.test(done[1])) {
    // fall through to the paths below
  } else if (done) {
    const tail = done[3].replace(SHOT_TAIL, '').replace(/[\s.,]+$/, '');
    if (!tail) return '';
    const aux = /\s(?:and|&)\s/.test(done[1]) ? 'were' : 'was';
    const line = `${done[1]} ${aux} ${done[2]} ${tail}.`;
    return /\b(shot|close-?up|two-shot|three-shot|establishing)\b/i.test(line) ? '' : line;
  }

  // ARTICLE-LED NEXT: the name pattern below matches a bare "A" or "The" as a one-word subject
  // and then takes the next word for the verb, so "A drum kit sits" parsed as subject "A", verb
  // "drum" and returned nothing. Falling through on '' keeps a genuine "The Metronome turns".
  if (/^(?:the|a|an)\s/i.test(t)) {
    const framed = articleFrame(t);
    if (framed) return framed;
  }
  const m = t.match(/^((?:[A-Z][\w'\u2019-]*)(?:\s+[A-Z][\w'\u2019-]*){0,2}(?:\s+(?:and|&)\s+[A-Z][\w'\u2019-]*)?|Both|They)\s+([a-z]+)\s+(.+)$/);
  if (!m) return '';
  const [, subject, verb, rest] = m;
  if (/['\u2019]s$/.test(subject)) return '';
  const plural = /\s(?:and|&)\s/.test(subject) || subject === 'Both' || subject === 'They';
  let head;
  if (['is', 'are', 'was', 'were'].includes(verb) && /^\w+ing\b/.test(rest)) head = plural ? 'were' : 'was';
  else if (plural && !verb.endsWith('s')) head = pastOf(verb);
  else { const st = stemOf(verb); head = st ? pastOf(st) : null; }
  if (!head) return '';
  // A SECOND FINITE VERB IN THE SAME CLAUSE. "stepped in close and speaks below the noise" mixes
  // tenses in one sentence; the Python weaver converts these and the first version of this port
  // forgot to. Named subjects are converted too ("and Cha Jin-woo turns"); a bare `, verbs` is NOT,
  // because that token is as often a plural noun and "four photographs" would become "photographed".
  const body = convertTail(rest.replace(SHOT_TAIL, '').replace(/[\s.,]+$/, ''));
  if (!body) return '';
  const line = `${subject} ${head} ${body}.`;
  return /\b(shot|close-?up|two-shot|three-shot|establishing)\b/i.test(line) ? '' : line;
}

// NARRATED SPEECH, matching worker/prose.py. The owner asked three times for one continuous story
// rather than narration with dialogue dropped into it. Reported speech — "Then come harder, I
// answered." — is still a quoted line with a tag welded on. Narrated, it is "Saya told Noe to come
// harder", and nothing is quoted so nothing is interspersed.
//
// The API now sends `pronoun` per line and `characters` per panel, which is what makes this
// possible in the browser: speaker, their person, and — with two people present — the listener.
const BACKSHIFT = { was:'had been', were:'had been', cannot:'could not', have:'had', has:'had',
  do:'did', does:'did', "don't":'did not', "doesn't":'did not', can:'could', "can't":'could not',
  will:'would', "won't":'would not', shall:'should', may:'might', must:'had to',
  know:'knew', look:'looked', want:'wanted', need:'needed', think:'thought' };
const NOT_A_VERB = new Set(('off on in at to from of the a an and or but with for by into out up down '
  + 'over under again well back here there now then too also never always only just still yet about '
  + 'after before between through during against among around behind beside beyond within without '
  + 'across along past since until upon than as if because while where when who whom whose which '
  + 'that my your his her its our their me him them us it this these those what why how '
  // Quantifiers: "it all pours" backshifted `all` as the verb and gave "it alled pours".
  + 'all both each every some any none most many much few several enough such other another '
  // Adverbs that do not end in -ly. "You already know why" narrated as "Baek alreadied know why";
  // the -ly ones are handled by rule in isAdverb, and this class is closed.
  + 'already perhaps maybe quite rather almost even soon often seldom indeed anyway somehow '
  + 'together instead ever once twice else alone apart aside ahead meanwhile tonight tomorrow '
  + 'yesterday today sometimes').split(' '));
// The few English verbs ending in -ly, so the rule below does not read them as adverbs.
const LY_VERBS = new Set(('apply comply imply multiply reply rely supply ply fly ally rally '
  + 'tally bully dally sully').split(' '));
// ADVERBS ARE A DIFFERENT SET FROM "CANNOT BE A VERB", and conflating them corrupted a noun.
// NOT_A_VERB answers "could this word be the verb here?" and so holds determiners and pronouns.
// convertTail uses isAdverb to STEP OVER words to reach the verb behind them — so "with her hood
// up and her hands" stepped over `her`, took `hands` for the verb, and produced "and her handed".
// Stepping needs the narrower question. Mirrors _ADVERBS / _is_adverb in worker/prose.py.
const ADVERBS = new Set(('already perhaps maybe quite rather almost even soon often seldom indeed '
  + 'anyway somehow together instead ever once twice else alone apart aside ahead meanwhile '
  + 'tonight tomorrow yesterday today sometimes again well back here there now then too also '
  + 'never always only just still yet').split(' '));

function isAdverb(word) {
  const low = word.toLowerCase().replace(/'$/, '');
  return ADVERBS.has(low) || (low.endsWith('ly') && !LY_VERBS.has(low));
}
const IMPERATIVE = /^(?:do not|don't|never|stop|go|come|get|give|take|put|tell|say|show|look|listen|wait|stand|sit|open|close|read|write|sign|bring|leave|hold|keep|let|move|step|turn|ask|answer|try|think|remember|forget|call|send|pour|drink|eat|walk|run|help|watch|find|check|explain|start|begin|finish|stay|return|follow|drop|pick|carry|push|pull|touch|count|name)\b/i;
const WH = /^(who|what|when|where|why|how|which|whose)\b/i;
const LEAD = /^(?:and|then|so|but|now|well|no|yes|okay|ok|please)[,\s]+/i;
const PAST_FORMS = new Set(Object.values(IRREG_PAST));

// Contractions must be expanded before the pronoun moves, or the verb is left behind: `\bI\b`
// matches inside "I'm" because an apostrophe is a word boundary, so "I'm so sorry" rendered as
// "they'm so sorry" and "You're the new intern" as "the other're the new intern" — live, in the
// first paragraphs of the second story in the catalogue. Only the persons that actually move are
// expanded; "he's"/"it's" are ambiguous between is and has and are never substituted.
const CONTRACTIONS = [
  [/\b(I|we|you)'m\b/gi, '$1 am'],
  [/\b(I|we|you)'re\b/gi, '$1 are'],
  [/\b(I|we|you)'ve\b/gi, '$1 have'],
  [/\b(I|we|you)'ll\b/gi, '$1 will'],
  [/\b(I|we|you)'d\b/gi, '$1 would'],
  // "let's" carries an `us` the shift has to see; left alone it reached the page as
  // "He told them to let's find out what theirs costs."
  [/\blet's\b/gi, 'let us'],
  // Third-person contractions, so reported speech can move them into the past. Only the
  // unambiguous ones: "he's" is `is` or `has` depending on what follows, so it is expanded only
  // before a present participle; "he'd" is left alone entirely.
  [/\b(he|she|it|they|that|there|who)'s\b(?=\s+\w+ing\b)/gi, '$1 is'],
  [/\b(he|she|it|they|that|there|who)'re\b/gi, '$1 are'],
  [/\b(he|she|it|they|that|there|who)'ve\b/gi, '$1 have'],
  [/\b(he|she|it|they|that|there|who)'ll\b/gi, '$1 will'],
  [/\bcan't\b/gi, 'cannot'],
  [/\bwon't\b/gi, 'will not'],
  [/\bshan't\b/gi, 'shall not'],
  [/\b(\w+)n't\b/gi, '$1 not'],
];

function expandContractions(text) {
  let t = text;
  for (const [rx, rep] of CONTRACTIONS) t = t.replace(rx, rep);
  return t;
}

const PRONOUN_POSSESSIVE = { me: ['my', 'mine'], us: ['our', 'ours'], them: ['their', 'theirs'],
  him: ['his', 'his'], her: ['her', 'hers'] };
// The SUBJECT form of a pronoun addressee: "You spent it all", reported about the narrator, is
// "said that I spent it all", not "said that me spent it all". A name is the same word either way.
const PRONOUN_SUBJECT = { me: 'I', us: 'we', them: 'they', him: 'he', her: 'she' };

function addresseeForm(text, endIndex, other) {
  if (!other) return 'the other';
  const subj = PRONOUN_SUBJECT[other.toLowerCase()];
  if (!subj) return other;
  const nxt = (text.slice(endIndex).trim().split(' ')[0] || '').replace(/[.,!?;:\u2026]+$/, '');
  return nxt && hasFiniteVerb(nxt) ? subj : other;
}

// A NAME takes "'s"; a PRONOUN has its own possessive and appending the clitic is never right.
function possessive(other, absolute) {
  if (!other) return absolute ? 'theirs' : 'their';
  const forms = PRONOUN_POSSESSIVE[other.toLowerCase()];
  if (forms) return absolute ? forms[1] : forms[0];
  return other + "'s";
}

// The absolute possessive is a different word, and first person was not using it: "that brush is
// mine" became "that brush is her" and "the shop is ours" became "the shop is their". `possessive`
// already got this right for the addressee ("your"/"yours"). Mirrors _ABSOLUTE_POSS in prose.py.
const ABSOLUTE_POSS = { my: 'mine', our: 'ours', her: 'hers', his: 'his',
  their: 'theirs', your: 'yours', its: 'its' };
const absolutePoss = (poss) => ABSOLUTE_POSS[(poss || '').toLowerCase()] || poss;

function shiftPerson(text, spk, other) {
  text = expandContractions(text);
  const map = { i: spk.subj, me: spk.obj, my: spk.poss, mine: absolutePoss(spk.poss),
    myself: spk.obj + 'self',
    we: 'they', us: 'them', our: 'their', ours: 'theirs',
    // "me's" is not a possessive: when the addressee is the NARRATOR, `other` is the pronoun "me",
    // and the clitic gave "for a boy me's size" in 141 panels. A pronoun has its own form.
    your: possessive(other), yours: possessive(other, true) };
  // The i flag is load-bearing: without it "You" never matches and the second person survives.
  return text.replace(/\b(I|me|my|mine|myself|we|us|our|ours|you|your|yours)\b/gi,
    (w, _g, off) => {
      const low = w.toLowerCase();
      // `you` is decided by POSITION, so it needs the offset the others do not.
      if (low === 'you') return addresseeForm(text, off + w.length, other);
      return Object.prototype.hasOwnProperty.call(map, low) ? map[low] : w;
    });
}

// Reported speech backshifts, and the verb after a replaced subject has to follow it: "You are a
// cup" must not narrate as "Yuna are a cup". Already-past verbs do not move again ("didded"), and
// not every word after a subject is a verb ("Yuna offed the squad").
// A pronoun is capitalised at the start of a sentence, so a case-sensitive match found nothing in
// "They do not know" and the clause kept its present tense. A NAME cannot take the same treatment:
// "Mark" would match "mark" in "leave a mark". The pronouns are a closed set, so the two are told
// apart rather than the whole thing being made case-insensitive.
const SUBJECT_PRONOUNS = new Set(['he', 'she', 'it', 'they', 'we', 'i', 'you', 'that', 'there', 'this']);

// The only words that may follow a subject `that`. Mirrors _THAT_VERBS in worker/prose.py.
const THAT_VERBS = new Set(['is','are','am','was','were','has','have','had','do','does','did',
  'will','would','can','cannot','could','shall','should','may','might','must']);

function backshiftAfter(text, subjects) {
  for (const w of subjects.filter(Boolean)) {
    const plural = ['they', 'we'].includes(w.toLowerCase());
    const flags = SUBJECT_PRONOUNS.has(w.toLowerCase()) ? 'gi' : 'g';
    // Up to three words may stand between subject and verb ("he really very nearly said"), so the
    // adverb run can be stepped over. Anything not rewritten is put back verbatim.
    const re = new RegExp('\\b(' + w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      + ")\\s+((?:[A-Za-z']+\\s+){0,3}[A-Za-z']+)\\b", flags);
    const step = (input) => input.replace(re, (m0, subj, run) => {
      const words = run.split(/\s+/);
      let i = 0;
      while (i < words.length && isAdverb(words[i])) i += 1;
      if (i >= words.length) return m0;
      const lead = words.slice(0, i).map((x) => ' ' + x).join('');
      const tail = words.slice(i + 1).map((x) => ' ' + x).join('');
      const verb = words[i];
      const low = verb.toLowerCase();
      // "that" IS A DETERMINER FAR MORE OFTEN THAN IT IS A SUBJECT, and the word after a
      // determiner is a NOUN. Read as a subject it gave "sit that chaired", "on that walled",
      // "in that booked" and "that hed had never been happier" -- 1,443 panels of broken words.
      // Only an auxiliary or a copula may follow a subject `that`: no available test tells a
      // relative pronoun ("the hand that answers it") from a determiner ("he read that book"),
      // and a closed set can only ever PREVENT an inflection, never invent one. Mirrors
      // _THAT_VERBS in worker/prose.py; the parity test pins the two together.
      if (w.toLowerCase() === 'that' && !THAT_VERBS.has(low)) return m0;
      if (PAST_FORMS.has(low) || low.endsWith('ed')
          || ['had','was','were','did','could','would','should','might','must'].includes(low)) return m0;
      if (NOT_A_VERB.has(low)) return m0;
      let nv;
      if (low === 'are') nv = plural ? 'were' : 'was';
      // "they" takes WERE, including the singular they used when a description states no pronoun.
      // Mapping am/is to "was" unconditionally gave "they was so sorry" once contractions started
      // expanding to "I am".
      else if (low === 'am' || low === 'is') nv = plural ? 'were' : 'was';
      else if (BACKSHIFT[low]) nv = BACKSHIFT[low];
      // A third-person -s form must be stemmed first, or "never knows" becomes "never knowsed".
      else if (/^[a-z]+$/.test(low)) nv = pastOf(stemOf(low) || low);
      else return m0;
      return subj + lead + ' ' + nv + tail;
    });
    // Repeated until stable: the four-word window swallows the NEXT subject, so
    // "he cannot stay and he will not leave" converted only its first half in one pass.
    for (let i = 0; i < 4; i += 1) {
      const next = step(text);
      if (next === text) break;
      text = next;
    }
  }
  return text;
}

function narrateSpeech(text, speaker, spk, other, solo = true) {
  let t = (text || '').trim().replace(/^[\u2026.\s]+/, '');
  if (!t) return null;
  const says = [], tells = [], asks = [], fragments = [];
  let fragmentsFirst = null;
  for (const raw of t.split(/(?<=[.!?\u2026])\s+/)) {
    const sent = raw.trim();
    if (!sent) continue;
    const body = sent.replace(LEAD, '').trim();
    if (!body) continue;
    const end = body[body.length - 1];
    const core = body.replace(/[.!?\u2026]+$/, '').trim();
    if (!core) continue;
    const bare = core.replace(/[,.!?]+$/, '').trim();
    // A vocative is ONE word, or the listener's own name. Two words is a sentence.
    if (!IMPERATIVE.test(core) && (bare.split(/\s+/).length === 1 || (other && bare === other))
        && !/\b(is|was|are|were|do|did|does|can|will|has|have|had|am|be)\b/i.test(bare)) continue;
    let shifted = shiftPerson(core, spk, other);
    // THE THIRD PERSON BACKSHIFTS TOO, not only the substituted subjects — a clause that was
    // already third person otherwise keeps the speaker's present tense inside the narrator's past
    // one. Same list as worker/prose.py.
    const subjects = [other, spk.subj, 'they'].filter(Boolean);
    for (const p of ['he', 'she', 'it', 'they', 'that', 'there']) {
      if (!subjects.includes(p)) subjects.push(p);
    }
    shifted = backshiftAfter(shifted, subjects);
    const first = shifted.split(' ')[0].replace(/[,.]$/, '');
    // "I" keeps its capital wherever it stands — the clause starts lower case after "said that",
    // but that rule cannot reach the one English word that is always capitalised.
    if (!(other && first === other) && first !== 'I') {
      shifted = shifted[0].toLowerCase() + shifted.slice(1);
    }
    // A verbless STATEMENT becomes its own sentence. A verbless question keeps the asked-form:
    // dropping the attribution there loses that it was put to someone.
    if (end === '?') { if (fragmentsFirst === null) fragmentsFirst = false; asks.push(shifted); }
    else if (!IMPERATIVE.test(core) && !hasFiniteVerb(core)) {
      if (fragmentsFirst === null) fragmentsFirst = true;
      fragments.push(shifted ? shifted[0].toUpperCase() + shifted.slice(1) : shifted);
    } else if (IMPERATIVE.test(core)) {
      if (fragmentsFirst === null) fragmentsFirst = false;
      const neg = shifted.match(/^(?:do not|don't)\s+(.*)$/i);
      tells.push(neg ? 'not to ' + neg[1] : 'to ' + shifted);
    } else { if (fragmentsFirst === null) fragmentsFirst = false; says.push(shifted); }
  }
  const out = [];
  if (says.length) out.push(`${speaker} said that ${says.join(', and that ')}.`);
  if (tells.length) out.push(`${out.length ? spk.subj[0].toUpperCase() + spk.subj.slice(1) : speaker} told ${other || 'them'} ${tells.join(', and ')}.`);
  if (asks.length) {
    const lead = WH.test(asks[0]) ? 'asked' : 'asked whether';
    out.push(`${out.length ? spk.subj[0].toUpperCase() + spk.subj.slice(1) : speaker} ${lead} ${asks.join(', and ')}.`);
  }
  // Fragments go where they were spoken; if the line OPENED on one, emitting it after the
  // attributed clause reverses the speaker's order.
  let frags = fragments.map((x) => (/[.!?\u2026]$/.test(x) ? x : x + '.'));
  // In a panel with two speakers a bare fragment reads as the other one's, so the FIRST is
  // attributed — words first, attribution after, the shape attribute() already uses.
  if (!solo && frags.length) frags = [attribute(frags[0], speaker, 'said'), ...frags.slice(1)];
  const all = fragmentsFirst ? [...frags, ...out] : [...out, ...frags];
  if (!all.length) return null;
  return all.map((x) => x[0].toUpperCase() + x.slice(1)).join(' ');
}

// A VERBLESS FRAGMENT IS NOT A CLAUSE, and "said that" needs a clause. 19.3% of the corpus's
// dialogue sentences carry no finite verb -- "Two streaks.", "Street ash.", "A prince." -- and each
// became "said that a prince". A fragment narrates as its own sentence instead.
// Mirrors _has_finite_verb in worker/prose.py; -ing is deliberately NOT a signal, because a
// participle needs an auxiliary to be finite and "nothing" ends in -ing too.
const AUXILIARIES = new Set(('is are was were am be been being had has have did does do will would '
  + 'can cannot could shall should may might must let').split(' '));

function hasFiniteVerb(core) {
  const t = expandContractions(core);
  if (/\w'(?:s|re|ve|ll|d|m)\b/.test(t)) return true;
  for (const w of (t.toLowerCase().match(/[a-z]+/g) || [])) {
    if (AUXILIARIES.has(w) || BACKSHIFT[w] || IRREG_PAST[w]) return true;
    if (PAST_FORMS.has(w) || w.endsWith('ed')) return true;
    if (HEAD_VERBS.has(w)) return true;
    const stem = stemOf(w);
    if (stem && (HEAD_VERBS.has(stem) || IRREG_PAST[stem])) return true;
  }
  return false;
}

// One line of speech as reported prose: the words first, the attribution after. Mirrors
// _attribute in worker/prose.py -- "Rin said, Not tonight." is a stage direction; prose puts the
// speech first and the attribution behind it.
function attribute(line, who, verb) {
  const tail = line[line.length - 1];
  if ('!?\u2026'.includes(tail)) return `${line} ${who} ${verb}.`;
  return `${tail === '.' ? line.slice(0, -1) : line}, ${who} ${verb}.`;
}

const SAID = ['said', 'answered', 'replied'];

// Who "you" means in this line, or null when it cannot be told. Mirrors _addressee in
// worker/prose.py, including the order of the steps and the "exactly one candidate" rule — a wrong
// name is worse than a vague one, so no step ever guesses between two.
//
// Measured on the corpus: panel cast alone leaves 46% of dialogue lines saying the literal words
// "the other". The chain takes the rendered panels from 14.2% to 6.2%.
function addressee(spkId, panelCast, panelSpeakers, chapterCast, prevSpeaker) {
  const pools = [panelCast, [...new Set([...panelCast, ...panelSpeakers])], chapterCast];
  for (const pool of pools) {
    const others = pool.filter((c) => c && c !== spkId);
    if (others.length === 1) return others[0];
  }
  if (prevSpeaker && prevSpeaker !== spkId) return prevSpeaker;
  return null;
}

// The speaker of the previous line, carried ACROSS panels: in an exchange, "you" is whoever just
// spoke, and that is as true over a panel break as inside one. Reset when a chapter is rendered.
let lastSpeaker = null;

function prose(p, staged) {
  const parts = [];
  if (staged) {
    const framed = p.narration ? '' : sceneSentence(p.scene);
    if (framed) parts.push(framed);
  }
  if (p.narration) parts.push(p.narration.trim());
  // Dialogue NARRATED, not quoted. Reported speech still put a line in the paragraph with a tag on
  // it; this says what was said. A line that will not classify cleanly keeps its own words rather
  // than being mangled — a wrong sentence is worse than a visible seam.
  const cast = p.characters || [];
  const speakers = (p.dialogue || []).map((d) => d.name || '');
  // The API sends the chapter cast as display names, the same strings panel.characters uses.
  const chapterCast = chapter?.characters || [];
  (p.dialogue || []).forEach((d, i) => {
    const who = d.name || '';
    // The API sends display names in `characters` and `dialogue[].name`, so no id lookup is needed
    // on this side; the weaver maps ids to names for the same reason.
    const other = addressee(who, cast, speakers, chapterCast, lastSpeaker);
    if (who) lastSpeaker = who;
    const subj = d.pronoun || 'they';
    const spk = { subj, obj: { she: 'her', he: 'him', they: 'them' }[subj] || 'them',
      poss: { she: 'her', he: 'his', they: 'their' }[subj] || 'their' };
    // A panel with one voice can leave a fragment unattributed; a panel with two cannot.
    const solo = new Set(speakers.filter(Boolean)).size <= 1;
    const narrated = who ? narrateSpeech(d.text, who, spk, other, solo) : null;
    if (narrated) { parts.push(narrated); return; }
    let line = (d.text || '').trim().replace(/^[\u2026.\s]+/, '');
    if (!line) return;
    if (!/[.!?\u2026]$/.test(line)) line += '.';
    parts.push(line);
  });
  return parts.length ? `<p class="prose">${esc(parts.join(' '))}</p>` : '';
}

function saveProgress(id, no) {
  if (!token) return;
  api(`/api/stories/${encodeURIComponent(id)}/progress`, { method: 'POST', body: JSON.stringify({ no }) })
    .catch(() => {});
}

// THE CINDER TRAIL. Scroll depth as a line burning down the edge of the screen — the story's own
// image doing the job a progress bar would do anyway.
function updateTrail() {
  const doc = document.documentElement;
  const max = doc.scrollHeight - doc.clientHeight;
  const pct = max > 0 ? Math.min(100, (doc.scrollTop / max) * 100) : 0;
  el('trail-fill').style.height = `${pct}%`;
  // Completion is a separate event from opening, because the gap between the two is the only
  // signal that says a chapter lost people rather than found them.
  if (!completed && pct > 92 && chapter) {
    completed = true;
    track('chapter.completed', { story_id: chapter.story_id, chapter_no: chapter.no });
    // The end of a chapter is the only honest moment to offer this: they just finished one and the
    // notification is about the next. Not awaited -- the offer must never delay the reader.
    maybeAskForPush().catch(() => {});
    maybeAskForRating(chapter.story_id, story?.title);
  }
}

// The canonical AI disclosure, fetched once and rendered into the reader. See index.html for why
// this is per chapter rather than only on the story page, and why the markup carries a fallback.
let disclosure = null;
async function paintDisclosure() {
  const node = el('reader-ai');
  if (!node) return;
  if (!disclosure) {
    disclosure = await api('/api/ai-disclosure').catch(() => null);
  }
  if (disclosure?.summary) node.textContent = disclosure.summary;
}

// ── the rating ask ─────────────────────────────────────────────────────────
//
// RATINGS EXISTED AT BOTH LEVELS AND NOTHING EVER ASKED FOR ONE. The form sits below the chapter
// and a reader has to decide, unprompted, that they are the kind of person who reviews things. So
// the counts stayed near zero while the feature looked finished.
//
// The moment is the end of a reader's THIRD chapter of a story: they have an opinion by then, and
// the form is already on screen, so the ask costs them a glance rather than a dialog. A modal would
// interrupt somebody mid-read, which is the one thing this product must not do to them.

const RATED_KEY = 'aic.rated';      // stories this reader has rated or waved away
const ASK_AFTER_CHAPTERS = 3;
const READ_KEY = 'aic.storyreads';  // chapters finished per story, this browser

function jsonStore(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch { return {}; }
}
function saveStore(key, v) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private mode: ask is lost, fine */ }
}

// markRatingAsked records that this story is settled -- answered or declined -- so the line never
// comes back for it.
function markRatingAsked(id) {
  const done = jsonStore(RATED_KEY);
  done[id] = 1;
  saveStore(RATED_KEY, done);
  el('chapcom-ask').hidden = true;
}

function maybeAskForRating(id, title) {
  const ask = el('chapcom-ask');
  if (!ask) return;
  ask.hidden = true;
  if (!token) return;                          // a rating needs an account to attach to
  if (jsonStore(RATED_KEY)[id]) return;        // answered or waved away already
  const reads = jsonStore(READ_KEY);
  reads[id] = (reads[id] || 0) + 1;
  saveStore(READ_KEY, reads);
  if (reads[id] < ASK_AFTER_CHAPTERS) return;
  el('chapcom-ask-text').textContent =
    `You have read ${reads[id]} chapters of ${title || 'this story'}. What would you give it?`;
  ask.hidden = false;
  track('rating.asked', { story_id: id });
}

el('chapcom-ask-no').onclick = () => {
  const id = el('chapcom')?.dataset.story;
  if (id) markRatingAsked(id);
  track('rating.ask_declined');
};

// ── push notifications ─────────────────────────────────────────────────────
//
// THE SERVER COULD ALREADY SEND AND NOTHING COULD RECEIVE. notifyUser, the FCM sender, the device
// token store and the per-kind cadence rules were all built and tested; no client had ever called
// /api/notifications/register, so there were zero tokens and every push was delivered to nobody.
// This is the missing half.

const PUSH_ASKED = 'aic.push.asked';   // we have shown OUR sheet, whatever they answered
const PUSH_TOKEN = 'aic.push.token';   // the last token we successfully registered
// Do not ask on somebody's first chapter. A reader who has finished two has shown us the habit the
// notification is for; asking before that spends a one-shot permission on a stranger.
const PUSH_ASK_AFTER_CHAPTERS = 2;
const PUSH_READ_COUNT = 'aic.push.reads';

// BOTH SERVER VALUES ARE REQUIRED, and the sender id is the one it is tempting to treat as
// optional. getToken needs it, and fails only AFTER Notification.requestPermission has resolved --
// so a deploy missing FCM_SENDER_ID would spend a reader's one-shot permission, which no API can
// ever ask for again, and hand back nothing. The server's own comment states the rule this follows:
// asking for permission we cannot then honour is worse than not asking.
function pushSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator
    && !!authConfig?.vapid_key && !!authConfig?.sender_id;
}

// maybeAskForPush runs at the end of a chapter. Every reason to stay silent is checked here rather
// than inside the sheet, so the sheet only ever opens when it is genuinely wanted.
async function maybeAskForPush() {
  if (!token) return;                                   // nothing to attach a device to
  await loadAuthConfig();
  if (!pushSupported()) return;
  if (Notification.permission !== 'default') {
    // Already granted: make sure the token is still registered -- FCM rotates them, and a rotated
    // token that is never re-sent is a reader who silently stops hearing anything.
    if (Notification.permission === 'granted') registerForPush().catch(() => {});
    return;
  }
  if (localStorage.getItem(PUSH_ASKED)) return;
  const reads = Number(localStorage.getItem(PUSH_READ_COUNT) || 0) + 1;
  localStorage.setItem(PUSH_READ_COUNT, String(reads));
  if (reads < PUSH_ASK_AFTER_CHAPTERS) return;
  el('push-ask').showModal();
  track('push.prompt_shown');
}

el('push-no').onclick = () => {
  localStorage.setItem(PUSH_ASKED, '1');
  el('push-ask').close();
  track('push.prompt_declined');
};

el('push-yes').onclick = async () => {
  localStorage.setItem(PUSH_ASKED, '1');
  el('push-ask').close();
  // requestPermission must be called from the click, not after an await, or Safari treats it as
  // programmatic and refuses.
  const granted = await Notification.requestPermission();
  track('push.permission', { result: granted });
  if (granted !== 'granted') return;
  try {
    await registerForPush();
    toast('We will tell you when the next chapter lands.');
  } catch (err) {
    // A reader who said yes and hears nothing has no way to discover why, so say it plainly.
    toast('Notifications could not be set up on this browser.');
    track('push.register_failed', { message: String(err && err.message || err).slice(0, 120) });
  }
};

// registerForPush gets an FCM token for this browser and hands it to our own API. Safe to call
// repeatedly: it only posts when the token has actually changed.
async function registerForPush() {
  const cfg = await loadAuthConfig();
  // The worker takes its Firebase config from this URL rather than hardcoding it -- one source of
  // truth, and a key rotation is a deploy instead of an edit in two files. The scope is pinned to
  // the root explicitly: the query string must not narrow what the worker controls.
  const q = new URLSearchParams({
    apiKey: cfg.api_key, projectId: cfg.project, senderId: cfg.sender_id || '',
  });
  const reg = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${q}`, { scope: '/' });
  const [appMod, msgMod] = await Promise.all([
    import('https://www.gstatic.com/firebasejs/10.12.2/firebase-app.js'),
    import('https://www.gstatic.com/firebasejs/10.12.2/firebase-messaging.js'),
  ]);
  const app = appMod.getApps().length ? appMod.getApps()[0]
    : appMod.initializeApp({ apiKey: cfg.api_key, projectId: cfg.project, messagingSenderId: cfg.sender_id });
  const fcmToken = await msgMod.getToken(msgMod.getMessaging(app), {
    vapidKey: cfg.vapid_key,
    serviceWorkerRegistration: reg,
  });
  if (!fcmToken) throw new Error('no token issued');
  if (localStorage.getItem(PUSH_TOKEN) === fcmToken) return;
  await api('/api/notifications/register', {
    method: 'POST',
    body: JSON.stringify({ token: fcmToken, platform: 'web' }),
  });
  localStorage.setItem(PUSH_TOKEN, fcmToken);
  track('push.registered');
}

// ── paywall + auth ─────────────────────────────────────────────────────────
// THE PRICE LIST, FETCHED ONCE.
//
// /api/pricing is regionalised from the edge — the same header /api/auth/config reads for the phone
// dial code — so it must not be cached across regions by anything but the browser, and it changes
// only on a deploy. One fetch per session is right.
let pricing = null;
let chosenSKU = 'sub_monthly';

// TEMPORARY, BY REQUEST, AND MEANT TO COME OUT.
//
// The owner asked on 2026-09-18 for each thumbnail to say how much of the story actually exists —
// "manga - how many, narrted video - how many, this will be removed later, only keeping it for my
// purposes for now". So it is deliberately one fetch, one function and one line in the card, all
// marked, and it is removed by deleting this block, the editionLine() call in the grid, and
// backend/internal/api/editions.go.
//
// It renders NOTHING until the counts arrive and nothing at all if the fetch fails: a card that
// briefly says "0 chapters" about a finished story is worse than a card that says nothing.
let editions = null;

async function loadEditions() {
  if (editions) return editions;
  try {
    const d = await api('/api/editions');
    editions = d.editions || {};
  } catch {
    editions = {};
  }
  return editions;
}

// "12/15 manga · 9/15 video" — a denominator, because 12 alone does not say whether that is all of
// it. Absent counts render as an empty string rather than zeros.
function editionLine(id) {
  const e = editions && editions[id];
  if (!e || !e.chapters) return '';
  return `<p class="card__editions">${e.manga}/${e.chapters} manga · ${e.video}/${e.chapters} video</p>`;
}

async function loadPricing() {
  if (pricing) return pricing;
  try {
    pricing = await api('/api/pricing');
  } catch {
    // A PAYWALL WITH NO PRICE IS STILL A PAYWALL. If this fails the sheet keeps its perks and its
    // Subscribe button and simply names no amount — which is what it did for everybody until now,
    // so it is a known-survivable state rather than a new failure mode.
    pricing = { plans: [] };
  }
  return pricing;
}

// A plan is one row: what it is, what it costs, and what that works out to. The middle column is
// the decision; the third is what stops ₹79 a week reading as cheaper than ₹199 a month.
function renderPlans(plans) {
  const box = el('paywall-plans');
  if (!box) return;
  if (!plans.length) { box.hidden = true; return; }
  // The recommended default is the monthly plan, by PERIOD and not by position — the server orders
  // the list and the client must not depend on that order, which is the same mistake that had the
  // annual saving computed against `plans[0]`.
  chosenSKU = (plans.find((p) => p.period === 'month') || plans[0]).sku;
  box.innerHTML = plans.map((p) => {
    // A separator, because the note and the rate are siblings in one line and CSS margin is
    // invisible to a screen reader reading them as one sentence.
    const per = p.per_month ? `<span class="plan__per">· ${esc(p.per_month)}/mo</span>` : '';
    const save = p.saving_percent ? `<span class="plan__save">saves ${p.saving_percent}%</span>` : '';
    const period = p.period ? `/${esc(p.period)}` : '';
    return `<li><button class="plan" type="button" data-sku="${esc(p.sku)}"
        aria-pressed="${p.sku === chosenSKU}">
      <span class="plan__name">${esc(p.name)}${save}</span>
      <span class="plan__price">${esc(p.display)}<span class="plan__period">${period}</span></span>
      <span class="plan__note">${esc(p.note || '')}${per}</span>
    </button></li>`;
  }).join('');
  box.hidden = false;
}

// Delegated on the list, because it is re-rendered whole.
el('paywall-plans').onclick = (e) => {
  const b = e.target.closest('[data-sku]');
  if (!b) return;
  chosenSKU = b.dataset.sku;
  el('paywall-plans').querySelectorAll('[data-sku]').forEach((x) =>
    x.setAttribute('aria-pressed', String(x.dataset.sku === chosenSKU)));
  track('paywall.plan_chosen', { sku: chosenSKU, story_id: el('paywall').dataset.story });
};

// NOTHING TO PRESS WHEN NOTHING CAN BE CHARGED.
//
// Reported by the owner on 2026-09-18: Subscribe "opens pay.mock and doesn't open anything". The
// server was answering /api/checkout with the mock provider's URL —
// https://pay.mock.local/checkout/<id>, a host that does not resolve — and the handler below does
// `location.href = c.url`, so the reader was navigated to a blank page.
//
// The server now refuses that checkout with 503 payments_unavailable, and /api/pricing carries
// `purchasable`. Both are needed: the refusal makes a dead navigation impossible, and this flag is
// what stops the reader reaching for it in the first place. The PRICES still render — a reader
// deciding whether this is for them is entitled to the numbers even on a day we cannot sell.
function applyPurchasable(can) {
  const btn = el('subscribe-btn');
  if (!btn) return;
  btn.hidden = !can;
  const note = el('paywall-unavailable');
  if (note) note.hidden = can;
}

function openPaywall(id, no) {
  el('paywall-sub').textContent = token
    ? `Chapter ${no} is part of the subscription. The first chapters of every story stay free.`
    : `Chapter ${no} needs a subscription. Sign in if you already have one.`;
  el('paywall').showModal();
  el('paywall').dataset.story = id;
  // After showModal, so a slow answer cannot delay the sheet appearing.
  loadPricing().then((d) => {
    renderPlans(d.plans || []);
    // UNDEFINED READS AS PURCHASABLE, deliberately. An older server that does not send the field
    // is one whose checkout works; treating a missing flag as "cannot buy" would take the paywall
    // away from a deployment where it functions.
    applyPurchasable(d.purchasable !== false);
  });
}

el('paywall-close').onclick = () => {
  track('paywall.dismissed', { story_id: el('paywall').dataset.story });
  el('paywall').close();
  history.back();
};

el('subscribe-btn').onclick = async () => {
  if (!token) {
    el('paywall').close();
    openAuth();
    return;
  }
  try {
    // THE SKU THE READER PICKED. This was hardcoded to sub_monthly, so the annual plan, the
    // weekly plan and the single-series purchase were all unreachable from the one screen that
    // asks for money — the server has sold four things and the client could buy one.
    const c = await api('/api/checkout', {
      method: 'POST',
      body: JSON.stringify({ sku: chosenSKU, story_id: el('paywall').dataset.story }),
    });
    if (c.url) location.href = c.url;
    else toast('Checkout is not available yet.');
  } catch (e) {
    // The 503 carries a sentence written for a reader ("Subscriptions are not on sale yet"), so
    // show that rather than a generic failure — and hide the button, because pressing it again
    // cannot work and the reader has no way to know that.
    if (e.status === 503) {
      applyPurchasable(false);
      toast(e.body?.message || 'Subscriptions are not on sale yet.');
    } else {
      toast(`Checkout failed. ${e.message}`);
    }
  }
};

// ── sign in ────────────────────────────────────────────────────────────────
// ONE FIELD, THEN THE STEP THAT FOLLOWS FROM IT.
//
// The sheet was three stacked forms: email+password, an "or", then a whole second phone form with
// its own heading and back button. It was the longest screen in a product whose entire job is
// reading, and it asked a reader to choose a MECHANISM before they had told us anything.
//
// Now they type a phone number or an email and we work out which it is. The second step — a texted
// code, or a password — appears only after that, so the sheet is never longer than the step it is
// on. Firebase Identity Platform is the provider behind both, which is why phone needs no password
// and email keeps ours.
let authConfig = null;
let confirmationResult = null;   // set once an SMS is away; holds the code verifier
let authStep = 'identify';       // identify -> code | password

// Deliberately loose. Anything with an @ is an email; anything that is mostly digits is a phone.
// A stricter test rejects valid addresses and unusual national formats, and the SERVER validates
// properly anyway — this only decides which second step to show.
const looksLikePhone = (v) => !v.includes('@') && (v.match(/\d/g) || []).length >= 6;

// FIREBASE ACCEPTS E.164 AND NOBODY TYPES E.164.
//
// signInWithPhoneNumber was handed the raw field value, so a reader in India typing the ten digits
// they have typed into every other form of their life got auth/invalid-phone-number — reported by
// the sheet as a problem with their number. The owner's requirement is that phone sign-in be
// "simple otp based", and demanding a format nobody uses conversationally is the opposite.
//
// The dial code comes from the server, which learns the country from the edge; the browser cannot
// work it out as well, because Intl gives a LANGUAGE and an en-GB browser in Mumbai would say +44.
//
// A LEADING ZERO IS A NATIONAL TRUNK PREFIX AND MUST GO. "020 7946 0018" in E.164 is
// "+442079460018", not "+4402079460018" — the zero exists only to tell a domestic exchange that a
// long-distance number follows. Dropping it is right for every country that uses one and harmless
// for the ones that do not, because they do not put a zero there.
//
// Returns '' when the number cannot be made unambiguous, so the caller asks rather than guesses.
// Sending an OTP to a number we assembled from a guessed country code means texting a stranger.
function toE164(typed, dialCode) {
  const raw = String(typed || '').trim();
  if (raw.startsWith('+')) return '+' + raw.slice(1).replace(/\D/g, '');
  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  // "00" IS AN INTERNATIONAL PREFIX, NOT A TRUNK ZERO. "0091 98765 43210" is the same number as
  // "+91 98765 43210" — 00 is what much of the world dials instead of a plus — so stripping it as
  // a leading zero would have produced "+91" + "9198765..." and texted a number that does not
  // exist. Handled before the dial code is consulted, because such a number already carries its
  // own country and needs no help.
  if (digits.startsWith('00')) {
    const rest = digits.slice(2).replace(/^0+/, '');
    return rest ? `+${rest}` : '';
  }
  if (!dialCode) return '';
  digits = digits.replace(/^0+/, '');
  return digits ? `${dialCode}${digits}` : '';
}

async function loadAuthConfig() {
  if (authConfig) return authConfig;
  try {
    authConfig = await api('/api/auth/config');
  } catch {
    // Password sign-in is served by the process that just failed to answer, but it is still the
    // honest fallback: never hide the one method that needs no external configuration.
    authConfig = { providers: ['password'] };
  }
  renderProviders();
  return authConfig;
}

// The provider buttons. Only what the server says is configured, so enabling Google later is a
// deployment change rather than a code change.
const PROVIDER_LABEL = { google: 'Continue with Google', apple: 'Continue with Apple', facebook: 'Continue with Facebook' };

// Brand marks, inline. A provider button without its mark reads as a generic link -- the owner
// asked for the icons by name -- and inlining them costs no request and cannot be blocked by a
// content policy the way an external sprite can. Paths are the official simple marks; the Google
// one keeps its four colours because a monochrome Google mark is off-brand and looks broken.
const PROVIDER_ICON = {
  google: '<svg class="pbtn__i" viewBox="0 0 18 18" aria-hidden="true" focusable="false">' +
    '<path fill="#4285F4" d="M17.6 9.2c0-.6-.05-1.2-.16-1.8H9v3.4h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9c1.7-1.6 2.7-3.9 2.7-6.5z"/>' +
    '<path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.2c-.8.5-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3A9 9 0 0 0 9 18z"/>' +
    '<path fill="#FBBC05" d="M3.9 10.7a5.4 5.4 0 0 1 0-3.4V5H.9a9 9 0 0 0 0 8l3-2.3z"/>' +
    '<path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 0 0 .9 5l3 2.3C4.6 5.2 6.6 3.6 9 3.6z"/></svg>',
  facebook: '<svg class="pbtn__i" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path fill="#1877F2" d="M24 12a12 12 0 1 0-13.9 11.9v-8.4H7.1V12h3V9.4c0-3 1.8-4.7 4.5-4.7 1.3 0 2.7.24 2.7.24v2.9h-1.5c-1.5 0-1.9.9-1.9 1.9V12h3.3l-.5 3.5h-2.8v8.4A12 12 0 0 0 24 12z"/></svg>',
  apple: '<svg class="pbtn__i" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" d="M16.4 12.8c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.15-2.8.85-3.5.85-.7 0-1.85-.83-3-.81-1.55.02-3 .9-3.8 2.28-1.6 2.8-.4 6.9 1.15 9.15.77 1.1 1.68 2.34 2.87 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.23-.02 2.01-1.12 2.76-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.64zM14.1 5.6c.63-.77 1.06-1.83.94-2.9-.91.04-2.01.61-2.66 1.37-.58.68-1.09 1.76-.95 2.8 1.01.08 2.04-.51 2.67-1.27z"/></svg>',
};

function renderProviders() {
  const wrap = el('auth-providers');
  const list = (authConfig?.providers || []).filter((p) => PROVIDER_LABEL[p]);
  wrap.innerHTML = list.map((p) =>
    `<button class="btn btn--ghost btn--block pbtn" type="button" data-provider="${esc(p)}">` +
    `${PROVIDER_ICON[p] || ''}<span>${esc(PROVIDER_LABEL[p])}</span></button>`).join('');
  wrap.hidden = !list.length;
  el('auth-or').hidden = !list.length;
}

function authError(msg) {
  el('auth-error').textContent = msg;
  el('auth-error').hidden = false;
}

// `sentTo` is the number an OTP actually went to, in full international form. It is shown back
// because the reader typed a number WITHOUT a country code and the client added one: if it added
// the wrong one, "no code arrived" and "it went to the wrong country" look identical, and only one
// of them is fixable by the reader.
function setAuthStep(step, sentTo) {
  authStep = step;
  el('auth-otp-field').hidden = step !== 'code';
  el('auth-pw-field').hidden = step !== 'password';
  el('auth-id').readOnly = step !== 'identify';
  el('auth-back').hidden = step === 'identify';
  el('auth-submit').textContent = step === 'identify' ? 'Continue' : 'Sign in';
  // The field itself, so it is impossible to miss and needs no new element.
  if (step === 'code' && sentTo) el('auth-id').value = sentTo;
  if (step === 'code') el('auth-otp').focus();
  if (step === 'password') el('auth-pw').focus();
}

// THE SKIP LINK THREW READERS OUT OF THE CHAPTER THEY WERE READING.
//
// `href="#main"` sets location.hash, and this is a HASH-ROUTED app: route() reads
// location.hash.split('/'), so "#main" parses to no route at all and falls through to the
// catalogue. Measured in a browser from inside chapter 1 -- the reader view closed and the grid
// came back. So the first control a keyboard user meets, the one that exists to help them, ejected
// them from the content and lost their place.
//
// It also never moved focus. A fragment link only focuses its target if the target is focusable,
// and <main> was not, so activating it scrolled the page and left focus on <body> -- the next Tab
// went back to the top and walked the whole nav again. Three bugs in one element: this, the route
// reset, and #0000EE at 2.1:1 because the :focus rule set a background and no colour.
//
// preventDefault stops the hash change; focus() does the job the href only pretended to.
document.querySelector('.skip')?.addEventListener('click', (e) => {
  e.preventDefault();
  const main = el('main');
  if (!main) return;
  main.focus();
  main.scrollIntoView({ block: 'start' });
});

// EVERY SHEET CLOSES ON A BACKDROP TAP, AND THE SIGN-IN SHEET HAD NO WAY OUT AT ALL.
//
// A native <dialog> gives Escape and a focus trap for free, which is why these are dialogs. Escape
// is a KEYBOARD key. On a phone there is no Escape, and the sign-in sheet has no close control and
// no dismiss: measured in a browser, a click on the backdrop left it open, and its only buttons are
// Continue and a step-specific back link. So a reader who tapped Sign in out of curiosity could
// leave it only by reloading the page. That is a trap on the one screen standing between a reader
// and an account.
//
// A backdrop click lands on the DIALOG ELEMENT ITSELF -- a child click has that child as target --
// so comparing target to the dialog is the whole test. Applied to every sheet so they behave alike
// rather than one being the exception.
function closeOnBackdrop(id) {
  const d = el(id);
  if (!d) return;
  d.addEventListener('click', (e) => { if (e.target === d) d.close(); });
}
['auth', 'paywall', 'push-ask'].forEach(closeOnBackdrop);

function openAuth() {
  el('auth-error').hidden = true;
  el('auth-id').value = '';
  el('auth-otp').value = '';
  el('auth-pw').value = '';
  confirmationResult = null;
  setAuthStep('identify');
  el('auth').showModal();
  // Not awaited: the sheet opens instantly and the providers appear a moment later. Password and
  // phone are always usable, so nothing the reader needs is waiting on this call.
  loadAuthConfig();
}

el('auth-back').onclick = () => {
  confirmationResult = null;
  setAuthStep('identify');
  el('auth-id').focus();
};

// The Firebase SDK is fetched only when somebody actually uses phone sign-in. It is ~130 kB and
// most readers use email, so loading it on every page view taxes the majority for the minority.
async function firebaseAuth() {
  const cfg = await loadAuthConfig();
  if (!cfg.project || !cfg.api_key) throw new Error('phone sign-in is not configured');
  const [appMod, authMod] = await Promise.all([
    import('https://www.gstatic.com/firebasejs/10.12.2/firebase-app.js'),
    import('https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js'),
  ]);
  const app = appMod.getApps().length ? appMod.getApps()[0]
    : appMod.initializeApp({ apiKey: cfg.api_key, authDomain: `${cfg.project}.firebaseapp.com`, projectId: cfg.project });
  return { auth: authMod, instance: authMod.getAuth(app) };
}

// Exchange a Firebase ID token for OUR session token, so every other endpoint has one notion of a
// session regardless of how the reader signed in.
async function adoptFirebaseUser(cred) {
  const idToken = await cred.user.getIdToken();
  const r = await api('/api/auth/firebase', { method: 'POST', body: JSON.stringify({ id_token: idToken }) });
  finishSignIn(r, 'Signed in.');
}

function finishSignIn(r, message) {
  token = r.token;
  localStorage.setItem(KEY, token);
  me = r.user;
  el('auth').close();
  refreshMe().then(() => { toast(message); route(); });
}

el('auth-providers').onclick = async (e) => {
  const b = e.target.closest('[data-provider]');
  if (!b) return;
  // CLEARED FIRST. Without this the sheet keeps the previous attempt's message, and the message it
  // keeps is the most misleading one there is: measured live, a Facebook attempt displayed "The
  // sign-in window closed before it finished" that belonged to an ABANDONED Google attempt, while
  // the Facebook popup was still open and working. Every other submit path clears it; this one did
  // not, and a stale error on the one screen between a reader and an account reads as a real
  // failure of the provider they just picked.
  el('auth-error').hidden = true;
  try {
    const { auth, instance } = await firebaseAuth();
    const providers = { google: auth.GoogleAuthProvider, apple: auth.OAuthProvider, facebook: auth.FacebookAuthProvider };
    const Ctor = providers[b.dataset.provider];
    const provider = b.dataset.provider === 'apple' ? new Ctor('apple.com') : new Ctor();
    await adoptFirebaseUser(await auth.signInWithPopup(instance, provider));
  } catch (err) {
    authError(providerErrorText(b.dataset.provider, err));
  }
};

// The owner clicked Google and Facebook and was told "request is invalid" — Firebase's own wording,
// passed through because this catch printed err.message verbatim. That tells a reader nothing and
// implies they did something wrong. Each case now says what is true and what to do instead.
function providerErrorText(which, err) {
  const name = (PROVIDER_LABEL[which] || 'That').replace('Continue with ', '');
  const code = `${err?.code || ''} ${err?.message || ''}`.toLowerCase();
  if (code.includes('popup-closed') || code.includes('cancelled-popup')) {
    return 'The sign-in window closed before it finished. Try again.';
  }
  if (code.includes('popup-blocked')) {
    return 'Your browser blocked the sign-in window. Allow pop-ups for this site, then try again.';
  }
  if (code.includes('unauthorized-domain')) {
    return `${name} sign-in is not authorised for this address yet. Use email or phone for now.`;
  }
  // operation-not-allowed / configuration-not-found / invalid-request all mean the same thing to a
  // reader: the provider is listed but not finished in the console. Say that, not "invalid".
  if (/operation-not-allowed|configuration|not-enabled|invalid[- ]request|invalid[- ]argument/.test(code)) {
    return `${name} sign-in is not finished being set up yet. Use email or phone for now — we are fixing it.`;
  }
  if (code.includes('account-exists-with-different-credential')) {
    return `You already have an account with that email using a different sign-in. Use that one and they will be linked.`;
  }
  if (code.includes('network')) {
    return 'That sign-in could not reach the network. Check your connection and try again.';
  }
  return `${name} sign-in failed. Use email or phone for now.`;
}

el('auth-form').onsubmit = async (e) => {
  e.preventDefault();
  const btn = el('auth-submit');
  btn.disabled = true;
  el('auth-error').hidden = true;
  const id = el('auth-id').value.trim();
  try {
    if (authStep === 'identify') {
      if (looksLikePhone(id)) {
        const cfg = await loadAuthConfig();
        const e164 = toE164(id, cfg.dial_code);
        if (!e164) {
          // Said BEFORE the round trip, because the alternative is Firebase rejecting a number we
          // assembled wrongly and the reader being told their number is wrong.
          authError('Add your country code to the number, like +91 98765 43210.');
          return;
        }
        const { auth, instance } = await firebaseAuth();
        // A fresh verifier per attempt: one that has already solved a challenge cannot be reused,
        // and reusing it fails with a stale-token error that reads to the user as "wrong number".
        const verifier = new auth.RecaptchaVerifier(instance, 'auth-recaptcha', { size: 'invisible' });
        confirmationResult = await auth.signInWithPhoneNumber(instance, e164, verifier);
        // The reader is about to be asked for six digits sent to a number they typed without a
        // country code. Showing the number we actually texted is the difference between "no code
        // arrived" and "I can see it went to the wrong number".
        setAuthStep('code', e164);
      } else {
        setAuthStep('password');
      }
      return;
    }
    if (authStep === 'code') {
      await adoptFirebaseUser(await confirmationResult.confirm(el('auth-otp').value.trim()));
      return;
    }
    // Password. Sign in, and fall back to creating the account — a reader typing their email and a
    // password does not care which of the two verbs applies, and asking them to pick was a whole
    // extra button on the old sheet.
    const body = JSON.stringify({ email: id, password: el('auth-pw').value });
    try {
      finishSignIn(await api('/api/auth/login', { method: 'POST', body }), 'Signed in.');
    } catch (err) {
      if (err.status !== 401) throw err;
      // The ref rides on REGISTER only -- a returning reader signing in has no referrer.
      const ref = takeRef();
      const regBody = ref
        ? JSON.stringify({ email: id, password: el('auth-pw').value, ref })
        : body;
      finishSignIn(await api('/api/auth/register', { method: 'POST', body: regBody }),
        'Account created.');
    }
  } catch (err) {
    const code = err?.code || '';
    authError(
      code.includes('invalid-phone-number') ? 'That does not look like a phone number. Include the country code, like +91.'
        : code.includes('invalid-verification-code') ? 'That code did not match. Check the six digits and try again.'
          : code.includes('code-expired') ? 'That code expired. Go back and request a new one.'
            : code.includes('too-many-requests') ? 'Too many attempts from this device. Try again later.'
              : err.status === 409 ? 'An account already exists for that email, and that password does not match it.'
                : err.status === 400 ? 'That password is too short — it needs at least 8 characters.'
                : `Could not sign in. ${err.message || code}`);
  } finally {
    btn.disabled = false;
  }
};

async function refreshMe() {
  if (!token) { me = null; }
  else { me = await api('/api/me').catch(() => { token = ''; localStorage.removeItem(KEY); return null; }); }
  el('account-btn').textContent = me ? (me.subscribed ? 'Subscribed' : 'Your account') : 'Sign in';
  el('shelf-link').hidden = !me;
}

// SIGNED OUT -> the sheet. SIGNED IN -> the account panel.
//
// This used to sign the reader out when they clicked it, which is what the owner hit: Google
// sign-in completed, the label became "Your account", and the only control that acknowledged the
// session destroyed it. Destroying a session is not something a button labelled with your own
// account should do, so it moved inside the panel where it is labelled "Sign out".
el('checkin-btn').onclick = doCheckIn;

el('account-btn').onclick = () => {
  if (!me) return openAuth();
  location.hash = '#/account';
};

el('signout-btn').onclick = () => {
  token = '';
  localStorage.removeItem(KEY);
  me = null;
  refreshMe();
  toast('Signed out.');
  location.hash = '#/';
};

// ── routing ────────────────────────────────────────────────────────────────
// The static pages are sections already present in index.html — no fetch, no template, nothing to
// go wrong offline — so routing to one is only a matter of which section is not hidden.
const PAGES = {
  about: 'About',
  'what-we-do': 'What we do',
  pricing: 'Pricing',
  contact: 'Contact',
  privacy: 'Privacy',
  terms: 'Terms',
};
const DEFAULT_TITLE = 'Sagapic — serialized stories';

// `lore` is a view, not a PAGES entry: PAGES are static pages with a fixed title, and this one
// takes its subject from the story it belongs to.
const views = { catalog: 'view-catalog', story: 'view-story', reader: 'view-reader',
  lore: 'view-lore', library: 'view-library', account: 'view-account' };
for (const p of Object.keys(PAGES)) views[p] = `view-${p}`;

function show(name) {
  for (const [k, id] of Object.entries(views)) el(id).hidden = k !== name;
  // A view change can take a playing narration off-screen; the player has to follow the reader.
  updatePlayerPlacement();
}
// ---------------------------------------------------------------------------------------------
// Analytics (GA4, stream sagapic_web / G-3513Q4NJ01)
//
// The owner asked to know how many people land, which story they click, which chapter they watch
// and where they drop off. Those are four questions and only the first is answered by a page view,
// so the events below are named for the questions rather than for the pages.
//
// LOADED AFTER FIRST PAINT, NEVER BLOCKING. gtag.js is a third-party script on a site whose LCP is
// already the thing being fixed; injecting it on idle keeps it out of the critical path entirely.
// Every call is wrapped because an ad blocker removing gtag must not take a reader's chapter with
// it -- analytics failing is invisible, a reader losing the page is not.
//
// DO NOT TRACK IS HONOURED. A reader who has asked not to be measured is not measured, and the
// site works identically for them.
const GA_ID = 'G-3513Q4NJ01';
let gaReady = false;

function gaInit() {
  try {
    if (gaReady) return;
    if (navigator.doNotTrack === '1' || window.doNotTrack === '1') return;
    gaReady = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    // send_page_view false: this is a hash-routed single page, so gtag's own automatic page view
    // fires once and then never again. route() sends them instead, which is the only way the
    // second and third story a reader opens are counted at all.
    window.gtag('config', GA_ID, { send_page_view: false });
    const t = document.createElement('script');
    t.async = true;
    t.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(t);
  } catch (_) { /* analytics must never break the page */ }
}

// NAMED ga(), NOT track(). There is already a track() at the top of this file that beacons to
// /api/events, and a second `function track` silently replaced it by hoisting -- every existing
// event (app.opened, paywall.shown, chapter.opened) would have gone to GA4 instead, under names
// containing dots that GA4 does not accept. Two analytics sinks, two functions.
function ga(event, params) {
  try {
    if (!gaReady || typeof window.gtag !== 'function') return;
    window.gtag('event', event, params || {});
  } catch (_) { /* as above */ }
}

// Drop-off needs a denominator and a last-seen point, so a chapter reports how FAR it was read
// rather than merely that it was opened. Fired at quarter marks, once each, and reset per chapter:
// without the reset every chapter after the first would look fully read.
let readDepth = { key: '', sent: new Set() };
function trackReadDepth(storyId, no) {
  const key = `${storyId}/${no}`;
  if (readDepth.key !== key) readDepth = { key, sent: new Set() };
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;
  if (scrollable <= 0) return;
  const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
  for (const mark of [25, 50, 75, 100]) {
    if (pct >= mark && !readDepth.sent.has(mark)) {
      readDepth.sent.add(mark);
      ga('chapter_progress', { story_id: storyId, chapter: Number(no), percent: mark });
    }
  }
}

const go = (hash) => { location.hash = hash; };

// ── the reader's own shelf ─────────────────────────────────────────────────
//
// FOUR COLUMNS AND A FIFTH THAT IS NOT LIKE THEM. Viewed, in progress, saved and liked are facts
// about the reader. Suggestions are a proposal, and the server returns an empty list rather than an
// arbitrary slice of the catalogue when it has nothing to go on — see api.suggest.
const SHELVES = [
  { key: 'in_progress', title: 'Carry on', note: 'Stories you are part-way through' },
  { key: 'saved',       title: 'Saved',    note: 'Kept for later' },
  { key: 'liked',       title: 'Liked',    note: 'Stories you liked' },
  { key: 'viewed',      title: 'Looked at', note: 'Recently opened' },
  { key: 'suggestions', title: 'You might like', note: 'From the genres you actually choose' },
];

async function loadLibrary() {
  const box = el('library-shelves');
  const empty = el('library-empty');
  if (!token) {
    box.innerHTML = '';
    empty.textContent = 'Sign in to keep a shelf of what you are reading.';
    empty.hidden = false;
    return;
  }
  empty.hidden = true;
  let d;
  try {
    d = await api('/api/me/library');
  } catch (e) {
    box.innerHTML = '';
    empty.textContent = `Your shelf could not be loaded. ${esc(e.message)}`;
    empty.hidden = false;
    return;
  }
  // A shelf with nothing on it says so ONCE, rather than printing five empty headings — five
  // headings over nothing looks like a broken page, not a new account.
  const any = SHELVES.some((sh) => (d[sh.key] || []).length);
  if (!any) {
    box.innerHTML = '';
    empty.textContent = 'Nothing here yet. Open a story and it will appear.';
    empty.hidden = false;
    return;
  }
  box.innerHTML = shelvesHTML(d);
}

// ONE RENDERER FOR BOTH SHELF SURFACES. #/library and #/account show the same five shelves, and
// two copies of this template is two places for "Saved" to start meaning different things.
function shelvesHTML(d) {
  return SHELVES.map((sh) => {
    const items = d[sh.key] || [];
    if (!items.length) return '';   // an empty column is omitted, not shown empty
    return `
      <section class="rail" aria-labelledby="shelf-${sh.key}">
        <h2 class="rail__h" id="shelf-${sh.key}">${esc(sh.title)}<span class="rail__note">${esc(sh.note)}</span></h2>
        <ol class="rail__row">${items.map((it, i) => `
          <li><button class="rcard" data-story="${esc(it.id)}">
            <span class="rcard__art"><img src="${coverURL(it.id)}" alt=""
              width="160" height="212" loading="${i < 3 ? 'eager' : 'lazy'}" decoding="async"></span>
            <span class="rcard__t">${esc(it.title)}</span>
            <span class="rcard__line">${cardLine(it)}</span>
            <span class="rcard__m">${shelfMeta(sh.key, it)}</span>
          </button></li>`).join('')}</ol>
      </section>`;
  }).join('');
}

// ACCOUNT. Identity, entitlement, the coins, the shelves and the reader's own comments.
//
// Every figure here comes from an endpoint that already existed except the comments, which needed
// GET /api/me/reviews — see the note on myReviews in backend/internal/api/reviews.go for why a
// panel over a story-keyed API would have been a heading with nothing under it.
// renderRewards draws the loyalty state.
//
// IT REPORTS WHAT IS LEFT TODAY, not just the cap. "You can earn 2 a day" and "you have 1 left
// today" are different sentences and only the second one tells a reader whether to do anything now.
//
// Every number is the server's. The daily cap, the per-action rates and what a coin buys are all
// served by GET /api/me/rewards for one reason: entitlement.CreditCost is what the unlock path
// actually charges, and a second copy here would eventually promise a price the server refuses.
function renderRewards(d, err) {
  const box = el('account-rewards');
  const btn = el('checkin-btn');
  if (!box) return;
  if (!d) {
    box.innerHTML = `<p class="empty">Your loyalty coins could not be loaded. ${esc(err?.message || '')}</p>`;
    if (btn) btn.hidden = true;
    return;
  }
  const cost = d.unlock_cost || {};
  const next = d.next_milestone || {};
  const rows = [
    ['Coins', String(d.balance ?? 0)],
    // A streak of 0 is a prompt, not a statistic.
    ['Streak', d.streak_days ? `${d.streak_days} day${d.streak_days === 1 ? '' : 's'}` : 'Start today'],
    ['Left to earn today', `${d.remaining_today ?? 0} of ${d.daily_cap ?? 0}`],
  ];
  if (next.days) {
    rows.push(['Next reward', `+${next.credits} at ${next.days} days`]);
  }
  if (cost.manga) {
    // What the coins are FOR. A balance with no price beside it is a number a reader cannot act on.
    rows.push(['Unlocks', `${cost.story || 1} coin a prose chapter · ${cost.manga} a manga chapter`]);
  }
  // THE INVITE, and the numbers beside it are the server's.
  const refBtn = el('refer-btn');
  const code = d.referral_code || '';
  if (code) {
    rows.push(['Invites', `${d.referrals_paid ?? 0} of ${d.referrals_max ?? 0} · +${d.rates?.referral ?? 0} each`]);
  }
  if (refBtn) {
    // Hidden at the ceiling for the same reason the check-in button hides once claimed: a control
    // that cannot do anything is noise.
    refBtn.hidden = !code || (d.referrals_paid ?? 0) >= (d.referrals_max ?? 0);
    refBtn.onclick = () => shareReferral(code, d);
  }

  // EVERY ROW IS COLLECTED BEFORE ANYTHING IS RENDERED. The first version of this assigned
  // box.innerHTML, then pushed the Invites row, then assigned it again — correct output from a
  // double render, with the cell template written out twice so the two copies could drift.
  box.innerHTML = rows.map(([k, v]) =>
    `<div class="acct__cell"><div class="acct__k">${esc(k)}</div><div class="acct__v">${esc(v)}</div></div>`).join('');

  if (btn) {
    // Hidden rather than disabled once claimed: a control that cannot do anything is noise, and
    // the streak row above already says the reader has been here today.
    btn.hidden = !!d.checked_in_today;
    btn.textContent = d.streak_days ? `Claim today's coin · ${d.streak_days}-day streak` : "Claim today's coin";
  }
}

// shareReferral hands over the reader's own invite link.
//
// SAYS WHAT IT PAYS AND WHEN. "Invite a friend" with no terms is how a reader ends up feeling
// cheated: the referrer earns nothing until the friend has actually come back on
// referral_qualifies separate days, so the message says so rather than implying a signup pays.
//
// Reuses the existing share plumbing -- the native sheet on mobile, clipboard everywhere else --
// so this needs no second implementation of "get a link to a person".
async function shareReferral(code, d) {
  const url = `${location.origin}/?ref=${encodeURIComponent(code)}`;
  const days = d?.referral_qualifies ?? 0;
  const mine = d?.rates?.referral ?? 0;
  const text = days
    ? `Read with me on Sagapic. You get a coin for joining; I get ${mine} once you have read on ${days} different days.`
    : 'Read with me on Sagapic.';
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Sagapic', text, url });
      track('referral.invited', { method: 'native' });
      return;
    } catch (err) {
      // A dismissed sheet is not a failure and is not counted.
      if (err?.name === 'AbortError') return;
    }
  }
  await copyShareLink(url, 'referral.invited', {});
}

// The daily check-in. A BUTTON RATHER THAN AN AUTOMATIC CLAIM ON PAGE LOAD: the owner asked for
// check-in as something that promotes the app, and a reward taken silently on load teaches nobody
// to come back. Claiming it is the habit.
//
// The server shares one ledger key between this and the reading streak, so a reader who already
// read a chapter today gets 0 here -- correctly, and without the client needing to know the rule.
async function doCheckIn() {
  const btn = el('checkin-btn');
  if (!token || !btn) return;
  btn.disabled = true;
  try {
    const out = await api('/api/me/checkin', { method: 'POST' });
    const n = out?.credit_earned || 0;
    toast(n > 0 ? `+${n} coin${n === 1 ? '' : 's'} · ${out.streak_days}-day streak` : 'Already claimed today');
    // Re-read rather than patching the panel from the response: the balance, the streak and the
    // remaining allowance all moved, and the server is the only thing that knows all three.
    await loadAccount();
  } catch (e) {
    toast(e?.message || 'Could not claim today just now');
  } finally {
    btn.disabled = false;
  }
}

async function loadAccount() {
  const facts = el('account-facts');
  const box = el('account-shelves');
  const cmts = el('account-comments');
  const empty = el('account-empty');
  if (!token) {
    facts.innerHTML = '';
    box.innerHTML = '';
    cmts.innerHTML = '';
    empty.textContent = 'Sign in to see your account.';
    empty.hidden = false;
    el('account-sub').textContent = 'Not signed in.';
    return;
  }
  empty.hidden = true;
  // PARALLEL, and each failure is its own. A comments outage must not blank the coins.
  const [meR, libR, revR, rewR] = await Promise.allSettled([
    api('/api/me'), api('/api/me/library'), api('/api/me/reviews'), api('/api/me/rewards'),
  ]);
  const m = meR.status === 'fulfilled' ? meR.value : null;
  if (m) me = m;

  el('account-sub').textContent = m?.email ? m.email : 'Signed in.';
  facts.innerHTML = [
    ['Email', m?.email ? esc(m.email) : '—'],
    ['Subscription', m ? (m.subscribed ? 'Active' : 'None') : '—'],
    // "Loyalty coins" is the owner's word for what the ledger calls credits. Naming it twice in the
    // UI would make a reader wonder whether they have two balances.
    ['Loyalty coins', m ? String(m.credits ?? 0) : '—'],
    ['Member', m?.id ? `#${esc(String(m.id).slice(0, 8))}` : '—'],
  ].map(([k, v]) => `<div class="acct__cell"><div class="acct__k">${esc(k)}</div><div class="acct__v">${v}</div></div>`).join('');

  renderRewards(rewR.status === 'fulfilled' ? rewR.value : null, rewR.reason);

  if (libR.status === 'fulfilled') {
    const d = libR.value;
    const any = SHELVES.some((sh) => (d[sh.key] || []).length);
    box.innerHTML = any ? shelvesHTML(d)
      : '<p class="empty">Nothing on your shelves yet. Open a story and it will appear.</p>';
  } else {
    box.innerHTML = `<p class="empty">Your shelves could not be loaded. ${esc(libR.reason?.message || '')}</p>`;
  }

  if (revR.status === 'fulfilled') {
    const rows = revR.value.reviews || [];
    cmts.innerHTML = rows.length
      ? rows.map((r) => `
        <button class="cmt" data-story="${esc(r.story_id)}">
          <span class="cmt__top">${'★'.repeat(Math.max(0, Math.min(5, r.rating)))}${
            r.kind === 'chapter' && r.ref ? ` · chapter ${esc(r.ref)}` : ''} · ${esc(fmtDate(r.created_at))}</span>
          <span class="cmt__t">${r.text ? esc(r.text) : '(rating only)'}</span>
        </button>`).join('')
      : '<p class="empty">You have not written anything yet.</p>';
  } else {
    cmts.innerHTML = `<p class="empty">Your comments could not be loaded. ${esc(revR.reason?.message || '')}</p>`;
  }
}

// A date a reader recognises. Never toLocaleString() with no arguments: that quietly follows the
// browser locale, so the same comment reads "9/16/2026" for one reader and "16/09/2026" for
// another, and neither matches the rest of this page.
function fmtDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// The one number that belongs on each shelf. "Chapter 7 of 15" is the whole reason a Carry-on shelf
// exists; a saved story has not been started, so its own length is what matters.
function shelfMeta(key, it) {
  if (key === 'in_progress' && it.last_read) {
    return `chapter ${it.last_read}${it.chapters ? ` of ${it.chapters}` : ''}`;
  }
  return it.chapters ? `${it.chapters} chapter${it.chapters === 1 ? '' : 's'}` : '';
}

// SAVE, which store.Follow() has been able to do since it was written and nothing could reach.
let savedNow = false;
async function loadSaved(id) {
  const b = el('save-btn');
  if (!b) return;
  // Offered only to a signed-in reader: a Save button that opens a sign-in sheet is a button that
  // does not do what it says.
  b.hidden = !token;
  if (!token) return;
  try {
    const d = await api('/api/me/library');
    savedNow = (d.saved || []).some((x) => x.id === id);
  } catch { savedNow = false; }
  paintSaved();
}

function paintSaved() {
  const b = el('save-btn');
  if (!b) return;
  b.textContent = savedNow ? 'Saved' : 'Save';
  b.setAttribute('aria-pressed', String(savedNow));
}

el('save-btn').onclick = async () => {
  if (!story || !token) return;
  const next = !savedNow;
  try {
    await api(`/api/me/saved/${encodeURIComponent(story.id)}`, { method: next ? 'PUT' : 'DELETE' });
    savedNow = next;
    paintSaved();
    track(next ? 'story.saved' : 'story.unsaved', { story_id: story.id });
  } catch (e) {
    toast(`Could not ${next ? 'save' : 'remove'} this. ${e.message}`);
  }
};

async function route() {
  const [, kind, id, no] = location.hash.split('/');
  // The title is the tab, the bookmark and the back-button label. A hash-routed site that never
  // changes it makes every page in a reader's history indistinguishable.
  document.title = PAGES[kind] ? `${PAGES[kind]} — Sagapic` : DEFAULT_TITLE;
  // One page_view per route, with the hash as the path: a hash-routed site that sends only the
  // first view reports every reader as a single-page bounce however far they actually went.
  ga('page_view', {
    page_title: document.title,
    page_location: location.href,
    page_path: location.hash || '#/',
  });
  try {
    if (PAGES[kind]) {
      show(kind);
      window.scrollTo(0, 0);
    } else if (kind === 'read' && id && no) {
      // LOAD BEFORE SHOW. See the story branch below for what show-then-load did to a reader.
      if (!story || story.id !== id) await loadStory(id);
      show('reader');
      await loadChapter(id, no);
      ga('chapter_open', { story_id: id, chapter: Number(no), story_title: story?.title || '' });
    } else if (kind === 'library') {
      show('library');
      await loadLibrary();
      window.scrollTo(0, 0);
    } else if (kind === 'account') {
      show('account');
      await loadAccount();
      window.scrollTo(0, 0);
    } else if (kind === 'lore' && id) {
      if (!story || story.id !== id) await loadStory(id);
      show('lore');
      el('lore-sub').textContent = story?.title || '';
      el('lore-back').href = `#/story/${encodeURIComponent(id)}`;
      await loadLore();
      window.scrollTo(0, 0);
      ga('page_view', { page_title: document.title, page_location: location.href,
        page_path: location.hash || '#/' });
    } else if (kind === 'story' && id) {
      // SHOW ONLY ONCE THE STORY IS IN HAND, AND THIS ORDER IS THE WHOLE FIX.
      //
      // Measured on the live site 2026-09-27 with #/story/definitely-not-a-story/1. `show('story')`
      // ran first, `loadStory` then threw on the 404, and the catch below only raised a toast — so
      // the reader was left on the story page regardless:
      //   cold load  a fully-chromed story page with EMPTY title, premise and cover, and a
      //              live-looking "Start reading" button that leads nowhere
      //   warm page  the PREVIOUS story's content, under a URL naming a different one — which is
      //              worse, because nothing about it looks wrong
      // Neither said the story does not exist. A stale bookmark, a mistyped link or a shared link
      // to a renamed story all land here.
      await loadStory(id);
      show('story');
      window.scrollTo(0, 0);
      ga('story_open', { story_id: id, story_title: story?.title || '' });
    } else {
      show('catalog');
      if (!catalog) await loadCatalog();
      else renderGrid();
    }
  } catch (e) {
    // A FAILED ROUTE MUST NOT STRAND THE READER ON THE VIEW IT COULD NOT FILL. A toast on top of an
    // empty page reads as a slow page, not as a missing story, and the toast is gone in seconds
    // while the empty page stays.
    if (e && e.status === 404) toast('That story is not here any more.');
    else toast(`Something went wrong. ${e.message}`);
    try {
      show('catalog');
      if (!catalog) await loadCatalog();
      else renderGrid();
    } catch (_) {
      // The catalogue itself is unreachable, which the toast above has already reported. Better a
      // bare page than an exception inside the handler for an exception.
    }
  }
}

// Delegated, because the grid and chapter list are re-rendered whole.
document.addEventListener('click', (e) => {
  const card = e.target.closest('[data-story]');
  if (card) return go(`#/story/${card.dataset.story}`);
  const chap = e.target.closest('[data-chapter]');
  if (chap) return go(`#/read/${story.id}/${chap.dataset.chapter}`);
  const wd = e.target.closest('[data-world-id]');
  if (wd) {
    const wid = wd.dataset.worldId;
    // Pressing the active world again clears it, exactly as All does. A tab that cannot be switched
    // off is a trap when it is the only thing between the reader and the catalogue.
    const clear = wid === 'all' || wid === activeWorld;
    activeWorld = clear ? null : wid;
    // Still filters what is already loaded rather than refetching: the grid holds every story the
    // age ceiling allows, and a world's members are a subset of it by construction.
    activeWorldIds = clear ? null : new Set(wd.dataset.world.split(','));
    loadWorlds();                        // re-render the row so the selected tab is visible
    renderGrid();
    return;
  }
  const q = e.target.closest('[data-video]');
  if (q) {
    // The queue on screen belongs to the story on screen, and clicking it is how the reader hands
    // the player to a new one.
    const open = watchable();
    return playChapter(open, open.findIndex((c) => c.no === Number(q.dataset.video)), story.id);
  }
  const tab = e.target.closest('[data-tab]');
  if (tab) return showTab(tab.dataset.tab);
  const ed = e.target.closest('[data-edition]');
  if (ed) return setEdition(ed.dataset.edition);
  const chip = e.target.closest('[data-filter]');
  if (chip) {
    // Pressing the SELECTED tab clears it. With no All chip, this is the only way back to the whole
    // catalogue, so a tab that could not be switched off would strand the reader inside one origin.
    activeFilter = chip.dataset.filter === activeFilter ? 'all' : chip.dataset.filter;
    renderFilters(); renderGrid(); return;
  }
  const gchip = e.target.closest('[data-genre]');
  if (gchip) {
    // Toggling, not replacing: picking two genres means "either", which is what ticking two boxes
    // has always meant, and the server ORs them within the facet for the same reason.
    const g = gchip.dataset.genre;
    if (activeGenres.has(g)) {
      activeGenres.delete(g);
    } else {
      activeGenres.add(g);
      // Counted on SELECT only. Counting deselects too would let a reader who tried a genre once
      // and rejected it keep seeing it promoted, which is the opposite of learning.
      genreUse[g] = (genreUse[g] || 0) + 1;
      try { localStorage.setItem(GENRE_USE_KEY, JSON.stringify(genreUse)); } catch { /* private mode */ }
    }
    loadCatalog();
    return;
  }
  const nav = e.target.closest('[data-nav]');
  if (nav) return go(nav.dataset.nav === 'catalog' ? '#/' : `#/story/${story.id}`);
});

// Debounced so a query is not fired on every keystroke — 114 stories is fast, but the event is
// recorded and a per-character funnel is noise rather than data.
// renderArcs tells a reader where they are in a long story, and that there is more.
//
// The server has returned `arcs` — a span and an `unlocked` flag per part — since arcs were built,
// and no client ever rendered it. So a reader of a 149-chapter story was served 80 chapters with no
// indication that they were in Part 1 of 2, or that Part 2 opened when they finished. The gate
// worked and nothing told anyone it was there, which is the whole of the feature from the reader's
// side.
//
// A LOCKED PART IS NOT SHOWN AT ALL.
//
// It used to render as "Part 2 — Opens when you finish Part 1", on the reasoning that an explained
// lock is a promise while a blurred padlock is a nag. The owner's correction, and it is right: that
// sentence tells a reader something they already know. Parts are numbered and they come in order;
// "Part 2 unlocks after Part 1" is the only thing it could possibly mean.
//
// So the strip shows what a reader HAS, and a new part appears when they reach it. That also makes
// the strip a record of progress rather than a wall of what they have not done yet.
//
// A single visible part renders nothing: "Part 1" alone is noise, which is 150 of the 151 stories
// in this catalogue.
function renderArcs(arcs, resume) {
  const strip = el('story-arcs');
  if (!strip) return;
  const open = Array.isArray(arcs) ? arcs.filter((a) => a.unlocked) : [];
  if (open.length < 2) { strip.hidden = true; return; }
  const currentNo = open.find((a) => resume >= a.from && resume <= a.to)?.no
    ?? open[open.length - 1].no;
  strip.innerHTML = open.map((a) => {
    const cls = a.no === currentNo ? 'arc arc--current' : 'arc';
    return `<li class="${cls}"><span>${esc(a.title || `Part ${a.no}`)}</span>`
      + `<span class="arc__span">Chapters ${a.from}–${a.to}</span></li>`;
  }).join('');
  strip.hidden = false;
}

// ── describing what you want, and saying it out loud ───────────────────────
//
// /api/discover/ask has existed for a while, ranks the catalogue against a description in prose,
// and NOTHING CALLED IT -- one of ten routes no reader could reach. The search box sent everything
// to /api/search, which matches titles, genres and regions. So "a story about revenge in a royal
// court" searched for those five words as keywords and found a volleyball story.
//
// A DESCRIPTION IS NOT A KEYWORD, and the two need different endpoints. The discriminator is
// mechanical rather than clever: a query of four or more words, or one containing a function word
// a title never has ("about", "where", "who", "with"), is somebody describing what they want. One
// or two words is somebody typing a title. Getting it wrong is cheap either way -- both endpoints
// return stories -- so the rule stays simple rather than becoming a parser.
const DESCRIBING = /\b(about|where|who|with|like|something|story|stories|want|feels?|involving)\b/i;
function isDescription(q) {
  return q.split(/\s+/).filter(Boolean).length >= 4 || DESCRIBING.test(q);
}

// askCatalogue routes a described query to the ranking endpoint and paints the same grid.
async function askCatalogue(q) {
  const d = await api('/api/discover/ask', {
    method: 'POST',
    body: JSON.stringify({ q, max_age: maxAge }),
  });
  const stories = (d.results || []).map((r) => r.story).filter(Boolean);
  catalog = { ...catalog, stories };
  renderGrid();
  track('search.performed', { q, results: stories.length, mode: d.ai ? 'ai' : 'ranked' });
  return { count: stories.length, ai: !!d.ai };
}

// VOICE IS FEATURE-DETECTED, NOT ASSUMED. SpeechRecognition is absent in Firefox and in every
// browser on iOS; the button ships hidden and is revealed only where the API exists, because a mic
// that does nothing when tapped is worse than no mic at all.
const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
let listening = null;
if (SpeechRec && el('search-mic')) {
  el('search-mic').hidden = false;
  el('search-mic').onclick = () => {
    if (listening) { listening.stop(); return; }
    const rec = new SpeechRec();
    rec.lang = navigator.language || 'en-US';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    listening = rec;
    el('search-mic').setAttribute('aria-pressed', 'true');
    track('search.voice_started');
    const done = () => {
      listening = null;
      el('search-mic').setAttribute('aria-pressed', 'false');
    };
    rec.onresult = (ev) => {
      const said = ev.results?.[0]?.[0]?.transcript?.trim();
      if (!said) return;
      el('search-input').value = said;
      // Dispatched rather than called directly so voice takes exactly the path typing takes --
      // one code path to be correct instead of two that must agree.
      el('search-input').dispatchEvent(new Event('input', { bubbles: true }));
      track('search.voice_result', { words: said.split(/\s+/).length });
    };
    rec.onerror = (ev) => {
      done();
      // not-allowed is a denied microphone permission and is the reader's own choice, not a fault.
      if (ev.error !== 'aborted' && ev.error !== 'no-speech') {
        toast(ev.error === 'not-allowed'
          ? 'Microphone access is off for this site.'
          : 'Could not hear that. Try again.');
      }
    };
    rec.onend = done;
    try { rec.start(); } catch { done(); }
  };
}

let searchTimer;
el('search-form').addEventListener('input', (e) => {
  query = e.target.value.trim();
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    if (location.hash && location.hash !== '#/') { go('#/'); return; }
    // A description goes to the ranking endpoint; a word or two goes to the keyword search.
    // If ranking fails for any reason the keyword path still answers, so the box never dead-ends.
    if (query && isDescription(query)) {
      askCatalogue(query).catch(() => loadCatalog().catch((err) => toast(err.message)));
    } else {
      loadCatalog().catch((err) => toast(err.message));
    }
  }, 250);
});
el('search-form').addEventListener('submit', (e) => e.preventDefault());

markMissingCovers(el('grid'));
el('age-select').value = maxAge;
el('age-select').addEventListener('change', (e) => {
  maxAge = e.target.value;
  localStorage.setItem(AGE_KEY, maxAge);
  worlds = null;   // membership counts are computed AFTER the gate, so they change with it
  if (location.hash && location.hash !== '#/') go('#/');
  else loadCatalog().catch((err) => toast(err.message));
});

el('home').onclick = () => go('#/');
addEventListener('hashchange', route);

// Start analytics when the browser is otherwise idle, so a third-party script never competes with
// the first paint on a site whose LCP is the thing being fixed. requestIdleCallback is not in every
// browser, hence the timeout fallback.
(window.requestIdleCallback || ((f) => setTimeout(f, 2000)))(() => {
  gaInit();
  // The first route() has already run by now and its page_view was dropped because gtag was not
  // ready. Send it once, or every session under-counts its own landing page — the exact number the
  // owner asked for first.
  ga('page_view', {
    page_title: document.title, page_location: location.href, page_path: location.hash || '#/',
  });
});

addEventListener('scroll', () => {
  el('topbar').toggleAttribute('data-scrolled', scrollY > 8);
  if (!el('view-reader').hidden) {
    updateTrail();
    // Drop-off: how far into the chapter the reader actually got.
    const [, kind, sid, no] = location.hash.split('/');
    if (kind === 'read' && sid && no) trackReadDepth(sid, no);
  }
}, { passive: true });

// Keyboard is a first-class way to read a serialized story: one hand, arrow keys, next chapter.
addEventListener('keydown', (e) => {
  if (el('view-reader').hidden || e.metaKey || e.ctrlKey) return;
  if (e.key === 'ArrowRight' && chapter?.next) go(`#/read/${chapter.story_id}/${chapter.next}`);
  if (e.key === 'ArrowLeft' && chapter?.no > 1) go(`#/read/${chapter.story_id}/${chapter.no - 1}`);
});

track('app.opened', {});
refreshMe().then(route);

// OFFER ONLY THE VOICES THAT EXIST.
//
// The content endpoint reports which narrators a chapter was actually built in. A fixed two-button
// switcher would hand the reader a male option for a chapter whose male cut has not been produced
// yet, and the server would quietly serve the female one — the reader would press a button, hear
// no change, and conclude the site is broken. Same rule as the sign-in sheet.
async function loadNarrators(no, storyId) {
  const wrap = el('voices');
  if (!wrap) return;
  wrap.innerHTML = '';
  // Same reason videoSrc takes one: the voices offered belong to the chapter being PLAYED, and
  // while docked that is not the story whose page is open.
  const sid = storyId || playerStory || (story && story.id);
  if (!sid) return;
  let list = [];
  try {
    const c = await api(`/api/stories/${encodeURIComponent(sid)}/chapters/${no}/content`);
    list = c.narrators || [];
  } catch { list = []; }
  if (list.length < 2) return;          // nothing to choose between
  const cur = narratorFor(sid);
  wrap.innerHTML = `<span class="voices__label">Voice</span>` + list.map((who) =>
    `<button class="chip chip--voice" data-narrator="${esc(who)}"
       aria-pressed="${who === cur}">${who === 'male' ? 'Male' : 'Female'}</button>`).join('');
}

document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-narrator]');
  const sid = playerStory || (story && story.id);
  if (!b || !sid) return;
  setNarrator(sid, b.dataset.narrator);
  const v = el('reel');
  const at = v ? v.currentTime : 0;
  // Reload the SAME chapter in the new voice and resume where they were. Restarting the chapter
  // would punish somebody for changing their mind eight minutes in.
  if (v && currentNo != null) {
    v.src = videoSrc(currentNo, sid);
    v.addEventListener('loadedmetadata', function once() {
      v.removeEventListener('loadedmetadata', once);
      if (at > 0 && at < v.duration) v.currentTime = at;
      tryPlay(v);
    });
  }
  loadNarrators(currentNo, sid);
});

// COMMENTS.
//
// Every string that reaches innerHTML below is user-authored, so it goes through esc(). That is
// the whole reason this module builds markup with a helper instead of template literals inline:
// one un-escaped interpolation here is stored XSS on a public catalogue page, and comments are the
// only place in this client where a stranger's text is rendered.
//
// The API never sends a user id — a comment identifies its author by a per-story handle — so there
// is nothing here to leak even by accident.
let commentStory = null;
let commentRating = 0;

// The tab badge is loaded with the story, not with the panel, so a reader can see that a story has
// been talked about without opening it.
async function loadCommentCount(id) {
  const badge = el('comment-count');
  if (!badge) return;
  badge.hidden = true;
  try {
    const { count = 0 } = await api(`/api/stories/${encodeURIComponent(id)}/reviews/summary`);
    if (count > 0) {
      badge.textContent = count;
      badge.hidden = false;
    }
  } catch {
    // A catalogue that cannot count is still a catalogue: no badge rather than a zero that is
    // not true. Same rule as the like count.
  }
}

function starsOf(n) {
  // Clamp ONCE and derive the empty half from it. Clamping each repeat separately looks
  // equivalent and is not: starsOf(-3) then renders 5-(-3)=8 empty stars, so a bad row widens
  // the whole thread. The row cannot come through the API today, but it is one bad backfill away.
  const filled = Math.max(0, Math.min(5, Math.round(Number(n) || 0)));
  return '★'.repeat(filled) + '☆'.repeat(5 - filled);
}

function commentDate(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function commentMarkup(c) {
  return `<li class="comment" data-id="${esc(c.id)}">
    <div class="comment__head">
      <span class="comment__handle">${esc(c.handle)}</span>
      ${c.mine ? '<span class="comment__mine">You</span>' : ''}
      <span class="comment__stars" aria-label="${c.rating} out of 5">${starsOf(c.rating)}</span>
      <span class="comment__when">${esc(commentDate(c.created_at))}</span>
      ${c.mine ? '' : `<button class="comment__report" type="button" data-report="${esc(c.id)}">Report</button>`}
    </div>
    <p class="comment__text">${esc(c.text)}</p>
  </li>`;
}

async function renderComments() {
  const id = story?.id;
  if (!id) return;
  commentStory = id;

  // The compose box only exists for a signed-in reader; everyone else gets a line telling them
  // why, rather than a form that fails on submit.
  el('comment-form').hidden = !token;
  el('comment-signin').hidden = !!token;
  if (token) wireCommentForm(id);

  const thread = el('thread');
  let list = [];
  try {
    list = await api(`/api/stories/${encodeURIComponent(id)}/reviews`);
  } catch {
    thread.innerHTML = '<li class="comment"><p class="comment__text">Comments could not be loaded.</p></li>';
    return;
  }
  // Empty renders as an empty <ol>, which the stylesheet labels. Assigning '' rather than a
  // placeholder keeps :empty true.
  thread.innerHTML = (list || []).map(commentMarkup).join('');
  thread.querySelectorAll('[data-report]').forEach((b) => {
    b.onclick = () => reportComment(b.dataset.report);
  });
}

function paintStars(n) {
  commentRating = n;
  document.querySelectorAll('.star').forEach((b) => {
    // Filled UP TO the chosen star: a 4 reads as four filled stars. Setting only the nth would
    // render a single mark floating in a row of empties, which nobody reads as a rating.
    if (Number(b.dataset.star) <= n) b.setAttribute('data-on', '');
    else b.removeAttribute('data-on');
  });
  el('comment-submit').disabled = n === 0;
}

function wireCommentForm(id) {
  document.querySelectorAll('.star').forEach((b) => {
    b.onclick = () => paintStars(Number(b.dataset.star));
  });
  el('comment-form').onsubmit = (e) => {
    e.preventDefault();
    postComment(id);
  };
}

function commentNote(msg, isError) {
  const n = el('comment-note');
  n.textContent = msg;
  if (isError) n.setAttribute('data-error', '');
  else n.removeAttribute('data-error');
}

async function postComment(id) {
  const text = el('comment-text').value.trim();
  if (!commentRating) return;
  const btn = el('comment-submit');
  btn.disabled = true;
  commentNote('');
  try {
    await api(`/api/stories/${encodeURIComponent(id)}/reviews`, {
      method: 'POST',
      body: JSON.stringify({ rating: commentRating, text }),
    });
    el('comment-text').value = '';
    paintStars(0);
    commentNote('Posted.');
    track('story.reviewed', { story_id: id, rating: commentRating });
    // Answered. The ask has done its job and must not reappear for this story.
    markRatingAsked(id);
    await renderComments();
    await loadCommentCount(id);
  } catch (err) {
    // 422 is the moderation refusal and must read as a decision, not a network hiccup. The reader
    // keeps their text — clearing the box on a refusal loses what they wrote and is how a person
    // gives up on a comment entirely.
    commentNote(err.status === 422
      ? 'This comment cannot be posted.'
      : 'Could not post that just now.', true);
    btn.disabled = false;
  }
}

// ── chapter comments ───────────────────────────────────────────────────────
//
// SCOPED TO THE CHAPTER, AND PLACED AT THE END OF IT. The story tab keeps the story-level thread;
// this one belongs to the chapter the reader just finished. Two reasons, and the first is the one
// that matters: a story-level thread on a fifteen-chapter serial shows chapter-fifteen reactions to
// a reader on chapter two, and the endings are the part worth spoiling. The second is that a reader
// with something to say has it at the end of a chapter, not after navigating back to a tab.
//
// The star buttons carry their own class. paintStars() selects '.star' across the whole document,
// so reusing it here would repaint the story form's rating from the reader and submit whatever the
// last click left behind.
let chapRating = 0;

function paintChapStars(n) {
  chapRating = n;
  document.querySelectorAll('.chapstar').forEach((b) => {
    if (Number(b.dataset.star) <= n) b.setAttribute('data-on', '');
    else b.removeAttribute('data-on');
  });
  el('chapcom-send').disabled = n === 0;
}

function chapNote(msg, isError) {
  const n = el('chapcom-note');
  if (!n) return;
  n.textContent = msg;
  n.hidden = !msg;
  if (isError) n.setAttribute('data-error', '');
  else n.removeAttribute('data-error');
}

// Count and thread are fetched together because they are one claim: a badge saying 3 over an empty
// list is worse than no badge at all.
async function renderChapterComments(id, no) {
  const section = el('chapcom');
  if (!section) return;
  // The ask's dismiss button reads this: it fires from a click, long after the render that knew
  // which story was on screen.
  section.dataset.story = id;
  const thread = el('chapcom-thread');
  const badge = el('chapcom-count');
  badge.hidden = true;
  el('chapcom-form').hidden = !token;
  el('chapcom-signin').hidden = !!token;
  chapNote('');
  paintChapStars(0);

  const base = `/api/stories/${encodeURIComponent(id)}/chapters/${encodeURIComponent(no)}/reviews`;
  // The count comes from /summary rather than from list.length. They agree today and will not stay
  // agreeing: the thread endpoint is the one that will grow a page size, and the first chapter to
  // pass it would then show a badge that undercounts itself. The summary is also what the server
  // aggregates, so it is the number that matches anything else reporting on this thread.
  let list = [];
  let agg = null;
  try {
    [list, agg] = await Promise.all([api(base), api(`${base}/summary`)]);
  } catch {
    // A chapter that cannot load its thread still reads: an empty list, no error furniture in the
    // middle of the story. Same rule as the like count and the story badge.
    thread.innerHTML = '';
    return;
  }
  thread.innerHTML = (list || []).map(commentMarkup).join('');
  thread.querySelectorAll('[data-report]').forEach((b) => {
    b.onclick = () => reportComment(b.dataset.report);
  });
  if (agg && agg.count) {
    badge.textContent = `${agg.count} · ${starsOf(agg.avg)}`;
    badge.hidden = false;
  }
}

function wireChapterComments(id, no) {
  const stars = el('chapcom-stars');
  if (!stars) return;
  stars.innerHTML = [1, 2, 3, 4, 5]
    .map((i) => `<button class="chapstar" type="button" data-star="${i}" role="radio" aria-checked="false" aria-label="${i} star${i > 1 ? 's' : ''}">★</button>`)
    .join('');
  document.querySelectorAll('.chapstar').forEach((b) => {
    b.onclick = () => paintChapStars(Number(b.dataset.star));
  });
  paintChapStars(0);

  el('chapcom-form').onsubmit = async (ev) => {
    ev.preventDefault();
    if (!chapRating) return;
    const btn = el('chapcom-send');
    btn.disabled = true;
    chapNote('');
    try {
      await api(`/api/stories/${encodeURIComponent(id)}/chapters/${encodeURIComponent(no)}/reviews`, {
        method: 'POST',
        body: JSON.stringify({ rating: chapRating, text: el('chapcom-textarea').value.trim() }),
      });
      el('chapcom-textarea').value = '';
      chapNote('Posted.');
      track('chapter.reviewed', { story_id: id, chapter_no: Number(no), rating: chapRating });
      markRatingAsked(id);
      await renderChapterComments(id, no);
    } catch (err) {
      // The reader keeps their text on a moderation refusal, for the reason the story form does:
      // clearing the box is how a person gives up on a comment entirely.
      chapNote(err.status === 422
        ? 'This comment cannot be posted.'
        : 'Could not post that just now.', true);
      btn.disabled = false;
    }
  };
}

async function reportComment(reviewID) {
  if (!token) {
    go('#/account');
    return;
  }
  // A free-text reason box is a worse report than a fixed reason: the queue can only be triaged on
  // values it knows, and "other" is what the server falls back to anyway.
  const reason = window.prompt(
    'Report this comment. Reason: spam, harassment, sexual, violence, self_harm, hate, infringement, ai_undisclosed, other',
    'other');
  if (!reason) return;
  try {
    await api('/api/reports', {
      method: 'POST',
      body: JSON.stringify({ target_kind: 'review', target_id: reviewID, reason: reason.trim() }),
    });
    commentNote('Reported. Thank you.');
  } catch (err) {
    commentNote(err.status === 429
      ? 'You have filed a lot of reports today. Try again later.'
      : 'Could not file that report.', true);
  }
}
